import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Select,
    MenuItem,
    InputAdornment,
    Chip,
    Divider,
    Button,
    Stack,
    LinearProgress,
    CircularProgress,
    IconButton,
    Menu,
    Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import BoltIcon from '@mui/icons-material/Bolt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import MapIcon from '@mui/icons-material/Map';
import TimelineIcon from '@mui/icons-material/Timeline';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import LayersIcon from '@mui/icons-material/Layers';
import PlaceIcon from '@mui/icons-material/Place';
import Icon from '@mdi/react';
import { mdiSteering, mdiFilePdfBox, mdiMicrosoftExcel, mdiEmailArrowRight, mdiDelete, mdiEye } from '@mdi/js';

import {
    restoreVisibleAssetsMarkers,
    setVisibleAssetsMarkers,
    hasGensetVehiclesSelector,
} from '../../features/assets/assetsSlice';
import {
    disableMapToolbar,
    enableMapToolbar,
    zoomIn,
    zoomOut,
    changeGoogleMapTypeId,
    toggleZonePolygons,
    togglePlaceMarkers,
    setMapControl,
    setNeedBounding,
    setGensetViewportPadding,
    setGensetViewportClip,
    clearGensetViewportClip,
    setGensetFocusAssetId,
    setGensetTrackReportId,
    gensetViewportPaddingSelector,
    mapServiceSelector,
    googleMapTypeIdSelector,
    showZonePolygonsSelector,
    showPlaceMarkersSelector,
} from '../../features/map/mapSlice';
import { useridSelector } from '../../features/user/UserSlice';
import { liveTrackinglayoutSelector } from '../../features/layout/layoutSlice';
import { selectDieselPriceAllData } from '../../features/dieselprice/dieselPriceSlice';
import { fetchAllDieselPrices } from '../../features/dieselprice/dieselPriceApi';
import {
    selectGensetDefaults,
    selectGensetSetupState,
    selectGensetSetupLoading,
    selectGensetStatusState,
    selectGensetFastHydrationLoading,
    selectGensetFullHydrationLoading,
    selectGensetMergedVehicles,
    selectGensetSetupFleet,
    selectGensetStatusFleet,
    selectGensetRefreshActive,
    selectGensetLiveFlowActive,
    selectGensetHydrationProgress,
    resetGensetSession,
} from '../../features/genset/gensetSlice';
import { fetchGensetSetup } from '../../features/genset/gensetApi';
import {
    startGensetLiveFlow,
    stopGensetRefreshLoop,
    startGensetRuntimeLoop,
    stopGensetRuntimeLoop,
} from '../../features/genset/gensetFlow';
import {
    historyCustom,
    downloadTrack,
    deleteReport,
    trackDataSelector,
    gensetTrackCacheSelector,
    setGensetTrackCache,
    clearGensetTrackCache,
    setHighlightedDrive,
    removeHighlightedDrive,
    setHighlightedStop,
    removeHighlightedStop,
    highlightedDriveSelector,
    highlightedStopSelector,
} from '../../features/datacenter/historySlice';
import {
    reportIDsSelector,
    downloadReport,
    getReportList,
} from '../../features/datacenter/reportSlice';
import { getExcel, getPdf } from '../../features/datacenter/datacenterApi';
import GenfleetReportForm from '../reportView/GenfleetReportForm';
import GeneratorFleetCompact from '../reportView/reportTemplates/GeneratorFleetCompact';
import GetPdfForm from '../common/GetPdfForm';

const palette = {
    ink: '#102a43',
    muted: '#627d98',
    soft: '#f5f8fc',
    card: '#ffffff',
    accent: '#0ea5a0',
    accentDark: '#0b8d88',
    accentSoft: '#dff5f3',
    accentGlow: 'rgba(14, 165, 160, 0.18)',
    crimson: '#c1121f',
    amber: '#f59e0b',
    emerald: '#15803d',
    indigo: '#4f46e5',
    indigoSoft: 'rgba(79, 70, 229, 0.12)',
    border: '#d9e2ec',
};

const formatMinutes = (minutes) => {
    const min = Math.max(0, Number(minutes) || 0);
    const h = Math.floor(min / 60);
    const m = Math.floor(min % 60);
    if (h <= 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
};

const formatDurationVerbose = (minutes) => {
    const total = Math.max(0, Number(minutes) || 0);
    const days = Math.floor(total / 1440);
    const hours = Math.floor((total % 1440) / 60);
    const mins = Math.floor(total % 60);
    const parts = [];
    if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'Hour' : 'Hours'}`);
    if (mins > 0 || parts.length === 0) parts.push(`${mins} ${mins === 1 ? 'Minute' : 'Minutes'}`);
    return parts.join(' ');
};

const formatIndianNumber = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '--';
    return parsed.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const formatDateTime = (value) => {
    if (!value) return '--';
    const m = moment(value, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true);
    if (!m.isValid()) return '--';
    return m.format('h:mm a D MMM');
};

const getRunningEntry = (running, key) => {
    if (!running || !key) return null;
    const lower = key.toLowerCase();
    return running?.[key] || running?.[lower] || null;
};

const normalizeRunningStatus = (value) => {
    const status = String(value || 'normal').trim().toLowerCase();
    if (status === 'just_started' || status === 'sensor_issue') return status;
    return 'normal';
};

const getCurrentRunningEntries = (vehicle) => {
    const running = vehicle?.currentRunning || {};
    return ['Left', 'Right', 'Single'].map((key) => {
        const entry = getRunningEntry(running, key) || {};
        return {
            key,
            durationMin: Number(entry?.durationMin || 0),
            startTime: entry?.startTime || null,
            status: normalizeRunningStatus(entry?.status),
        };
    });
};

const hasCurrentRunningStatus = (vehicle, status) => {
    const normalized = normalizeRunningStatus(status);
    return getCurrentRunningEntries(vehicle).some((entry) => entry.status === normalized);
};

const isRunningNow = (vehicle) => {
    if (typeof vehicle?.runningNow === 'boolean') return vehicle.runningNow;
    return getCurrentRunningEntries(vehicle).some((entry) => entry.durationMin > 0);
};

const getRunningSummary = (vehicle) => {
    const entries = getCurrentRunningEntries(vehicle).filter((entry) => entry.durationMin > 0);

    if (!entries.length) {
        if (vehicle?.runningNow === true) {
            return { running: true, durationMin: 0, startTime: null, inferred: true };
        }
        return { running: false, durationMin: 0, startTime: null };
    }

    const durationMin = Math.max(...entries.map((entry) => entry.durationMin));
    const validStarts = entries
        .map((entry) => ({
            ts: entry.startTime ? moment(entry.startTime, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true) : null,
        }))
        .filter((entry) => entry.ts && entry.ts.isValid());
    const startTime = validStarts.length
        ? validStarts.sort((a, b) => a.ts.valueOf() - b.ts.valueOf())[0].ts.format('YYYY-MM-DD HH:mm:ss')
        : null;

    return { running: true, durationMin, startTime };
};

const getListRunningBadge = (vehicle, runningSummary) => {
    if (vehicle?.runningNow === true && hasCurrentRunningStatus(vehicle, 'sensor_issue')) {
        return {
            label: 'Issue',
            color: palette.crimson,
            bg: `${palette.crimson}22`,
            active: true,
        };
    }
    if (hasCurrentRunningStatus(vehicle, 'just_started')) {
        return {
            label: 'Just Started',
            color: palette.indigo,
            bg: palette.indigoSoft,
            active: true,
        };
    }
    if (runningSummary?.running) {
        return {
            label: 'Running',
            color: palette.emerald,
            bg: `${palette.emerald}22`,
            active: true,
        };
    }
    return {
        label: 'Off',
        color: palette.muted,
        bg: `${palette.muted}22`,
        active: false,
    };
};

const getLiveRunningStatusBadge = (entry, durationMin) => {
    const status = normalizeRunningStatus(entry?.status);
    if (status === 'sensor_issue') {
        return {
            label: 'Issue Detected',
            color: palette.crimson,
            bg: `${palette.crimson}22`,
        };
    }
    if (status === 'just_started') {
        return {
            label: 'Just Started',
            color: palette.indigo,
            bg: palette.indigoSoft,
        };
    }
    if (durationMin <= 0) {
        return {
            label: 'Off',
            color: palette.muted,
            bg: `${palette.border}66`,
        };
    }
    return null;
};

const getSensorHealthString = (sensorHealth, side) => {
    if (!sensorHealth) return 'Not Monitored';
    const lower = String(side || '').toLowerCase();
    const upper = lower.charAt(0).toUpperCase() + lower.slice(1);
    const value = sensorHealth?.[lower] || sensorHealth?.[upper] || sensorHealth?.[side];
    return value || 'Not Monitored';
};

const normalizeSensorStatus = (value) => String(value || '').trim().toLowerCase();

const getRunningSides = (vehicle) => {
    const running = vehicle?.currentRunning || {};
    const sides = ['left', 'right', 'single'];
    return sides.filter((side) => {
        const key = side.charAt(0).toUpperCase() + side.slice(1);
        const entry = getRunningEntry(running, key) || {};
        return Number(entry?.durationMin || 0) > 0;
    });
};

const getRunningSensorLabel = (vehicle) => {
    const runningSides = getRunningSides(vehicle);
    if (!runningSides.length) return null;
    if (runningSides.length === 1) {
        return getSensorHealthString(vehicle?.sensorHealth, runningSides[0]);
    }
    const statuses = runningSides.map((side) => normalizeSensorStatus(getSensorHealthString(vehicle?.sensorHealth, side)));
    const unique = Array.from(new Set(statuses));
    if (unique.length === 1) {
        return getSensorHealthString(vehicle?.sensorHealth, runningSides[0]);
    }
    return 'Mixed';
};

const getMovingLabel = (vehicle) => {
    if (typeof vehicle?.movingNow === 'boolean') return vehicle.movingNow ? 'Moving' : 'Stationary';
    return null;
};

const getCurrentAddress = (vehicle) => {
    return vehicle?.currentAddress?.net_addr || vehicle?.currentAddress?.fmtd_addr || '';
};

const MovingIndicator = ({ moving }) => {
    if (moving === null || moving === undefined) return null;
    if (moving) {
        return (
            <ArrowRightAltIcon
                sx={{
                    fontSize: 16,
                    color: palette.emerald,
                    '@keyframes movePulse': {
                        '0%': { transform: 'translateX(0)' },
                        '50%': { transform: 'translateX(4px)' },
                        '100%': { transform: 'translateX(0)' },
                    },
                    animation: 'movePulse 1.2s ease-in-out infinite',
                }}
            />
        );
    }
    return (
        <RadioButtonUncheckedIcon
            sx={{
                fontSize: 12,
                color: palette.amber,
            }}
        />
    );
};

const getCutoffSides = (vehicle) => {
    const sensorHealth = vehicle?.sensorHealth || {};
    const sides = ['left', 'right', 'single'];
    const cutoffSides = sides.filter((side) => {
        const status = normalizeSensorStatus(getSensorHealthString(sensorHealth, side));
        return status === 'cut-off' || status === 'cutoff';
    });
    if (!cutoffSides.length) return null;

    // Ensure monitoring is available (not all sides are Not Monitored/Not Installed)
    const monitored = sides.some((side) => {
        const status = normalizeSensorStatus(getSensorHealthString(sensorHealth, side));
        return status && !status.includes('not monitored') && !status.includes('not installed');
    });
    if (!monitored) return null;
    return cutoffSides;
};

const getCutoffType = (vehicle) => {
    const cutoffSides = getCutoffSides(vehicle);
    if (!cutoffSides || cutoffSides.length === 0) return null;
    if (cutoffSides.includes('single')) return 'full';
    const hasLeft = cutoffSides.includes('left');
    const hasRight = cutoffSides.includes('right');
    if (hasLeft && hasRight) return 'full';
    if (hasLeft || hasRight) return 'part';
    return null;
};

const getCutoffLabel = (vehicle) => {
    const cutoffSides = getCutoffSides(vehicle);
    if (!cutoffSides) return null;

    if (cutoffSides.includes('single')) {
        return { label: 'Sensor Cut-off: Single', severity: 'critical' };
    }
    const hasLeft = cutoffSides.includes('left');
    const hasRight = cutoffSides.includes('right');
    if (hasLeft && hasRight) return { label: 'Sensor Cut-off: Both', severity: 'critical' };
    if (hasLeft) return { label: 'Sensor Cut-off: Left', severity: 'warn' };
    if (hasRight) return { label: 'Sensor Cut-off: Right', severity: 'warn' };
    return null;
};

const resolveIssueTone = (issue) => {
    const severity = String(issue?.severity || '').toLowerCase();
    if (severity === 'blocking') {
        return { color: palette.crimson, bg: 'rgba(193, 18, 31, 0.12)' };
    }
    if (severity === 'warning' || severity === 'warn') {
        return { color: palette.amber, bg: 'rgba(245, 158, 11, 0.12)' };
    }
    if (severity === 'info') {
        return { color: palette.muted, bg: `${palette.border}66` };
    }
    return { color: palette.muted, bg: `${palette.border}66` };
};

const resolveActiveIssueTone = (issue) => {
    const severity = String(issue?.severity || '').toLowerCase();
    if (severity === 'blocking' || severity === 'critical') {
        return {
            color: '#991b1b',
            bg: '#fffafa',
            border: 'rgba(153, 27, 27, 0.24)',
            chipBg: '#fff5f5',
        };
    }
    return {
        color: '#b42318',
        bg: '#fffdfc',
        border: 'rgba(180, 35, 24, 0.22)',
        chipBg: '#fff7f5',
    };
};

const buildSensorItems = (section = {}) => {
    const items = [];
    Object.entries(section || {}).forEach(([side, data]) => {
        if (!data) return;
        const logic = Array.isArray(data.logic)
            ? data.logic.map((entry) => `${entry?.label ?? ''}: ${entry?.value ?? ''}`).filter(Boolean)
            : [];
        items.push({
            key: `${side}-${data?.parameter || data?.name || 'sensor'}`,
            side,
            name: data?.name || '--',
            parameter: data?.parameter || data?.param || '--',
            logic,
        });
    });
    return items;
};

const buildEventItems = (section = {}) => {
    const items = [];
    Object.entries(section || {}).forEach(([side, data]) => {
        const events = Array.isArray(data?.events) ? data.events : [];
        events.forEach((event, idx) => {
            items.push({
                key: `${side}-${event?.name || 'event'}-${idx}`,
                side,
                name: event?.name || '--',
                parameter: event?.parameter || '--',
                value: event?.value ?? '--',
            });
        });
    });
    return items;
};

const SectionBlock = ({ title, children }) => (
    <Paper sx={{ padding: 1.5, borderRadius: 2, border: `1px solid ${palette.border}`, background: palette.card }}>
        <Typography variant="subtitle2" sx={{ color: palette.ink, marginBottom: 1, fontWeight: 600 }}>
            {title}
        </Typography>
        {children}
    </Paper>
);

const getRuntimeIssues = (vehicle) => {
    return Array.isArray(vehicle?.runtimeIssues) ? vehicle.runtimeIssues : [];
};

const getSetupIssues = (vehicle) => {
    return Array.isArray(vehicle?.setupIssues) ? vehicle.setupIssues : [];
};

const getCombinedIssues = (vehicle, isAdmin) => {
    if (!vehicle) return [];
    if (!isAdmin) return getRuntimeIssues(vehicle);
    if (Array.isArray(vehicle?.combinedIssues)) return vehicle.combinedIssues;
    const setup = getSetupIssues(vehicle);
    const runtime = getRuntimeIssues(vehicle);
    if (!setup.length) return runtime;
    if (!runtime.length) return setup;
    const combinedMap = new Map();
    const extras = [];
    setup.forEach((issue) => {
        const code = issue?.code;
        if (code) {
            if (!combinedMap.has(code)) combinedMap.set(code, issue);
        } else {
            extras.push(issue);
        }
    });
    runtime.forEach((issue) => {
        const code = issue?.code;
        if (code) {
            combinedMap.set(code, issue);
        } else {
            extras.push(issue);
        }
    });
    return [...combinedMap.values(), ...extras];
};

const hasIssues = (vehicle, isAdmin) => {
    return getCombinedIssues(vehicle, isAdmin).length > 0;
};

const isTamperSuspected = (vehicle) => {
    const sensorHealth = vehicle?.sensorHealth || {};
    const strings = [
        sensorHealth.left,
        sensorHealth.right,
        sensorHealth.single,
        sensorHealth.Left,
        sensorHealth.Right,
        sensorHealth.Single,
    ].filter(Boolean);
    if (strings.some((value) => String(value).toLowerCase().includes('suspected'))) return true;
    const detail = sensorHealth.detail || {};
    return Object.values(detail).some((entry) => entry?.tampered);
};

const buildStatusCounts = (vehicles, isAdmin) => {
    const counts = {
        all: vehicles.length,
        running: 0,
        off: 0,
        overused: 0,
        malfunctionPart: 0,
        malfunctionFull: 0,
        issues: 0,
        todayActive: 0,
        moving: 0,
        stationary: 0,
    };
    vehicles.forEach((vehicle) => {
        const running = isRunningNow(vehicle);
        if (running) counts.running += 1;
        else counts.off += 1;
        if (vehicle?.overused) counts.overused += 1;
        const cutoffType = getCutoffType(vehicle);
        if (cutoffType === 'part') counts.malfunctionPart += 1;
        if (cutoffType === 'full') counts.malfunctionFull += 1;
        if (hasIssues(vehicle, isAdmin)) counts.issues += 1;
        const sumMinutes = Number(vehicle?.period?.sumHoursMin || 0);
        const unionMinutes = Number(vehicle?.period?.unionHoursMin || 0);
        const hadActivity = sumMinutes > 0 || unionMinutes > 0 || running;
        if (hadActivity) counts.todayActive += 1;
        if (vehicle?.movingNow === true) counts.moving += 1;
        if (vehicle?.movingNow === false) counts.stationary += 1;
    });
    return counts;
};

const buildCountEntries = (counts = {}) => {
    return Object.entries(counts || {})
        .map(([label, value]) => ({
            label: label ? String(label) : 'unknown',
            value: Number(value || 0),
        }))
        .sort((a, b) => {
            const diff = b.value - a.value;
            if (diff !== 0) return diff;
            return a.label.localeCompare(b.label);
        });
};

const resolveVehicleName = (vehicle) => {
    return vehicle?.vehicleName || vehicle?.name || vehicle?.imei || 'Unknown Vehicle';
};

const resolveSetupType = (vehicle) => {
    return vehicle?.gensetSetup || vehicle?.sensorSetup?.type || 'Unknown';
};

const resolveSides = (vehicle) => {
    const setup = String(resolveSetupType(vehicle)).toLowerCase();
    if (setup.includes('single')) return ['single'];
    if (setup.includes('double')) return ['left', 'right'];
    return ['left', 'right', 'single'];
};

const GensetDashboard = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const userId = useSelector(useridSelector);
    const layout = useSelector(liveTrackinglayoutSelector);
    const isAdmin = useSelector((state) => state?.user?.isAdmin);
    const hasGensetVehicles = useSelector(hasGensetVehiclesSelector);
    const dieselData = useSelector(selectDieselPriceAllData);
    const gensetDefaults = useSelector(selectGensetDefaults);
    const setupState = useSelector(selectGensetSetupState);
    const gensetSetupLoading = useSelector(selectGensetSetupLoading);
    const statusState = useSelector(selectGensetStatusState);
    const fastHydrationLoading = useSelector(selectGensetFastHydrationLoading);
    const fullHydrationLoading = useSelector(selectGensetFullHydrationLoading);
    const setupFleet = useSelector(selectGensetSetupFleet);
    const statusFleet = useSelector(selectGensetStatusFleet);
    const mergedVehicles = useSelector(selectGensetMergedVehicles);
    const refreshActive = useSelector(selectGensetRefreshActive);
    const liveFlowActive = useSelector(selectGensetLiveFlowActive);
    const hydrationProgress = useSelector(selectGensetHydrationProgress);
    const trackData = useSelector(trackDataSelector);
    const gensetTrackCache = useSelector(gensetTrackCacheSelector);
    const highlightedDrive = useSelector(highlightedDriveSelector);
    const highlightedStop = useSelector(highlightedStopSelector);
    const reportIDs = useSelector(reportIDsSelector);
    const reportDataMap = useSelector((state) => state.reports.reportData);
    const mapService = useSelector(mapServiceSelector);
    const mapTypeId = useSelector(googleMapTypeIdSelector);
    const gensetViewportPadding = useSelector(gensetViewportPaddingSelector);
    const showZonePolygons = useSelector(showZonePolygonsSelector);
    const showPlaceMarkers = useSelector(showPlaceMarkersSelector);

    const [search, setSearch] = useState('');
    const [filterMode, setFilterMode] = useState('all');
    const [selectedImei, setSelectedImei] = useState('');
    const [setupFilter, setSetupFilter] = useState('');
    const [issueFilter, setIssueFilter] = useState('');
    const [deviceTypeFilter, setDeviceTypeFilter] = useState('');
    const [listenerPortFilter, setListenerPortFilter] = useState('');
    const [truckVoltageFilter, setTruckVoltageFilter] = useState('');
    const [mapOpen, setMapOpen] = useState(false);
    const [trackOpen, setTrackOpen] = useState(false);
    const [trackingEnabled, setTrackingEnabled] = useState(true);
    const [trackRefreshing, setTrackRefreshing] = useState(false);
    const [timelineTab, setTimelineTab] = useState('timeline');
    const [listTab, setListTab] = useState('vehicles');
    const [reportFormOpen, setReportFormOpen] = useState(false);
    const [selectedReportId, setSelectedReportId] = useState(null);
    const [getPdfFormOpen, setGetPdfFormOpen] = useState(false);
    const [copyAnchorEl, setCopyAnchorEl] = useState(null);
    const prevUserId = useRef(userId);
    const rootRef = useRef(null);
    const mapViewportRef = useRef(null);
    const mapControlsRef = useRef(null);
    const focusScrollRef = useRef(null);
    const lastScrollTopRef = useRef(0);
    const prevSelectedImeiRef = useRef('');
    const trackingGestureRef = useRef({ active: false, startX: 0, startY: 0 });
    const [mapViewportRect, setMapViewportRect] = useState(null);
    const mapFrameRect = useMemo(() => {
        const frame = mapService === 'osm' ? layout?.osm : layout?.main;
        if (!frame) return null;
        const left = Number.parseFloat(frame.left);
        const top = Number.parseFloat(frame.top);
        const width = Number.parseFloat(frame.width);
        const height = Number.parseFloat(frame.height);
        if (![left, top, width, height].every(Number.isFinite)) return null;
        return {
            left,
            top,
            width,
            height,
            right: left + width,
            bottom: top + height,
        };
    }, [layout, mapService]);

    useEffect(() => {
        return () => {
            dispatch(setMapControl('asset'));
            dispatch(restoreVisibleAssetsMarkers());
            dispatch(enableMapToolbar());
        };
    }, [dispatch]);

    useEffect(() => {
        return () => {
            dispatch(stopGensetRefreshLoop());
            dispatch(stopGensetRuntimeLoop());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!dieselData) dispatch(fetchAllDieselPrices());
    }, [dieselData, dispatch]);

    useEffect(() => {
        if (prevUserId.current !== userId) {
            dispatch(stopGensetRefreshLoop());
            dispatch(stopGensetRuntimeLoop());
            dispatch(resetGensetSession());
            prevUserId.current = userId;
        }
    }, [dispatch, userId]);

    useEffect(() => {
        if (!isAdmin) {
            if (issueFilter) setIssueFilter('');
            if (deviceTypeFilter) setDeviceTypeFilter('');
            if (listenerPortFilter) setListenerPortFilter('');
            if (truckVoltageFilter) setTruckVoltageFilter('');
        }
    }, [isAdmin, issueFilter, deviceTypeFilter, listenerPortFilter, truckVoltageFilter]);

    const dieselPrice = useMemo(() => {
        const states = dieselData?.states || {};
        let value = null;
        for (const cities of Object.values(states)) {
            if (Object.prototype.hasOwnProperty.call(cities, 'Mumbai')) {
                const v = Number(cities?.['Mumbai']?.price);
                if (Number.isFinite(v)) { value = v; break; }
            }
        }
        if (value === null) {
            for (const cities of Object.values(states)) {
                for (const label of ['Navi Mumbai', 'Thane']) {
                    if (Object.prototype.hasOwnProperty.call(cities, label)) {
                        const v = Number(cities?.[label]?.price);
                        if (Number.isFinite(v)) { value = v; break; }
                    }
                }
                if (value !== null) break;
            }
        }
        return Number.isFinite(Number(value)) ? Number(value) : 90;
    }, [dieselData]);

    useEffect(() => {
        if (!userId || !hasGensetVehicles || refreshActive || liveFlowActive) return;
        if (statusState?.error && !statusState?.fetchedAt) return;
        dispatch(startGensetLiveFlow({
            setupPayload: { user_id: userId, isAdmin: Boolean(isAdmin) },
            hydrationPayload: {
                user_id: userId,
                overuseThresholdMin: gensetDefaults?.overuseThresholdMin ?? 240,
                tamperThresholdMin: gensetDefaults?.tamperThresholdMin ?? 360,
                genset_lph: gensetDefaults?.genset_lph ?? 2,
                diesel_price: dieselPrice,
            },
            intervalMs: 180000,
        }));
    }, [
        dispatch,
        userId,
        hasGensetVehicles,
        refreshActive,
        liveFlowActive,
        gensetDefaults,
        dieselPrice,
        isAdmin,
        statusState?.error,
        statusState?.fetchedAt,
    ]);

    useEffect(() => {
        if (!userId || !hasGensetVehicles) return;
        dispatch(startGensetRuntimeLoop({
            payload: { user_id: userId },
            intervalMs: 60 * 60 * 1000,
        }));
    }, [dispatch, userId, hasGensetVehicles]);

    const statusCounts = useMemo(() => buildStatusCounts(mergedVehicles, isAdmin), [mergedVehicles, isAdmin]);
    const genfleetReportIds = useMemo(() => {
        return reportIDs.filter((reportID) => reportDataMap?.[reportID]?.reportType === 'genfleet');
    }, [reportIDs, reportDataMap]);
    const selectedReportData = selectedReportId ? reportDataMap?.[selectedReportId] : null;
    const setupCounts = useMemo(() => {
        const fromFleet = Object.entries(setupFleet?.gensetSetupCounts || {});
        if (fromFleet.length) return fromFleet;
        const counter = new Map();
        mergedVehicles.forEach((vehicle) => {
            const setup = vehicle?.gensetSetup || vehicle?.sensorSetup?.type;
            if (!setup) return;
            counter.set(setup, (counter.get(setup) || 0) + 1);
        });
        return Array.from(counter.entries());
    }, [setupFleet, mergedVehicles]);
    const issueMessageMap = useMemo(() => {
        const map = new Map();
        mergedVehicles.forEach((vehicle) => {
            const issues = getCombinedIssues(vehicle, isAdmin);
            issues.forEach((issue) => {
                const code = issue?.code;
                if (!code) return;
                if (!map.has(code)) {
                    map.set(code, issue?.message || code);
                }
            });
        });
        return map;
    }, [mergedVehicles, isAdmin]);

    const resolveListTypeLabel = () => {
        if (issueFilter) {
            const issueLabel = issueMessageMap.get(issueFilter) || issueFilter;
            return `Issue - ${issueLabel}`;
        }
        if (deviceTypeFilter) return `Device Type - ${deviceTypeFilter}`;
        if (listenerPortFilter) return `Listener Port - ${listenerPortFilter}`;
        if (truckVoltageFilter) return `Truck Voltage - ${truckVoltageFilter}`;
        if (setupFilter) return `Setup Type - ${setupFilter}`;
        const filterLabels = {
            all: 'All',
            running: 'Running',
            off: 'Off',
            todayActive: 'Today Active',
            moving: 'Moving',
            stationary: 'Stationary',
            overused: 'Overused',
            malfunctionPart: 'Part Malfunction',
            malfunctionFull: 'Full Malfunction',
            issues: 'Issues',
            otherIssue: 'Other Issue',
        };
        return filterLabels[filterMode] || 'All';
    };

    const issueCounts = useMemo(() => {
        const counts = new Map();
        const meta = new Map();
        mergedVehicles.forEach((vehicle) => {
            const issues = getCombinedIssues(vehicle, isAdmin);
            const seen = new Set();
            issues.forEach((issue) => {
                const code = issue?.code;
                if (!code || seen.has(code)) return;
                seen.add(code);
                counts.set(code, (counts.get(code) || 0) + 1);
                if (!meta.has(code)) {
                    meta.set(code, {
                        severity: issue?.severity || null,
                        issueType: issue?.type || null,
                    });
                }
            });
        });
        const entries = Array.from(counts.entries()).map(([code, count]) => ({
            code,
            count,
            label: issueMessageMap.get(code) || code,
            severity: meta.get(code)?.severity || null,
            issueType: meta.get(code)?.issueType || null,
        }));
        entries.sort((a, b) => {
            const diff = b.count - a.count;
            if (diff !== 0) return diff;
            return a.label.localeCompare(b.label);
        });
        return entries;
    }, [mergedVehicles, issueMessageMap, isAdmin]);

    const metadataCounts = useMemo(() => {
        return {
            deviceTypes: buildCountEntries(setupFleet?.deviceTypeCounts),
            listenerPorts: buildCountEntries(setupFleet?.listenerPortCounts),
            truckVoltages: buildCountEntries(setupFleet?.truckVoltageCounts),
        };
    }, [setupFleet]);

    const matchesMetaFilter = (value, filter) => {
        const left = String(value ?? 'unknown').toLowerCase();
        const right = String(filter ?? '').toLowerCase();
        return left === right;
    };

    const filteredVehicles = useMemo(() => {
        let list = [...mergedVehicles];
        const q = String(search || '').trim().toLowerCase();
        if (q) {
            list = list.filter((vehicle) => resolveVehicleName(vehicle).toLowerCase().includes(q));
        }
        if (filterMode === 'running') {
            list = list.filter((vehicle) => isRunningNow(vehicle));
        } else if (filterMode === 'off') {
            list = list.filter((vehicle) => !isRunningNow(vehicle));
        } else if (filterMode === 'overused') {
            list = list.filter((vehicle) => Boolean(vehicle?.overused));
        } else if (filterMode === 'malfunctionPart') {
            list = list.filter((vehicle) => getCutoffType(vehicle) === 'part');
        } else if (filterMode === 'malfunctionFull') {
            list = list.filter((vehicle) => getCutoffType(vehicle) === 'full');
        } else if (filterMode === 'issues') {
            list = list.filter((vehicle) => hasIssues(vehicle, isAdmin));
        } else if (filterMode === 'otherIssue') {
            list = list.filter((vehicle) => {
                if (!hasIssues(vehicle, isAdmin)) return false;
                const cutoffType = getCutoffType(vehicle);
                return cutoffType !== 'part' && cutoffType !== 'full';
            });
        } else if (filterMode === 'todayActive') {
            list = list.filter((vehicle) => {
                const sumMinutes = Number(vehicle?.period?.sumHoursMin || 0);
                const unionMinutes = Number(vehicle?.period?.unionHoursMin || 0);
                return sumMinutes > 0 || unionMinutes > 0 || isRunningNow(vehicle);
            });
        } else if (filterMode === 'moving') {
            list = list.filter((vehicle) => vehicle?.movingNow === true);
        } else if (filterMode === 'stationary') {
            list = list.filter((vehicle) => vehicle?.movingNow === false);
        }
        if (setupFilter) {
            list = list.filter((vehicle) => {
                const label = vehicle?.gensetSetup || vehicle?.sensorSetup?.type;
                return label === setupFilter;
            });
        }
        if (issueFilter) {
            list = list.filter((vehicle) => {
                const issues = getCombinedIssues(vehicle, isAdmin);
                return issues.some((issue) => issue?.code === issueFilter);
            });
        }
        if (deviceTypeFilter) {
            list = list.filter((vehicle) => {
                return matchesMetaFilter(vehicle?.additionalInfo?.deviceType, deviceTypeFilter);
            });
        }
        if (listenerPortFilter) {
            list = list.filter((vehicle) => {
                return matchesMetaFilter(vehicle?.additionalInfo?.listenerPort, listenerPortFilter);
            });
        }
        if (truckVoltageFilter) {
            list = list.filter((vehicle) => {
                return matchesMetaFilter(vehicle?.additionalInfo?.truckVoltage, truckVoltageFilter);
            });
        }
        list.sort((a, b) => {
            const ar = isRunningNow(a) ? 1 : 0;
            const br = isRunningNow(b) ? 1 : 0;
            if (br !== ar) return br - ar;
            return resolveVehicleName(a).localeCompare(resolveVehicleName(b));
        });
        return list;
    }, [
        mergedVehicles,
        search,
        filterMode,
        setupFilter,
        issueFilter,
        deviceTypeFilter,
        listenerPortFilter,
        truckVoltageFilter,
        isAdmin,
    ]);

    const handleSetupFilterClick = (label) => {
        setFilterMode('all');
        setIssueFilter('');
        setDeviceTypeFilter('');
        setListenerPortFilter('');
        setTruckVoltageFilter('');
        setSetupFilter((prev) => (prev === label ? '' : label));
    };

    const handleIssueFilterClick = (code) => {
        setFilterMode('all');
        setSetupFilter('');
        setDeviceTypeFilter('');
        setListenerPortFilter('');
        setTruckVoltageFilter('');
        setIssueFilter((prev) => (prev === code ? '' : code));
    };

    const closeMapView = useCallback(() => {
        setMapOpen(false);
        setTrackOpen(false);
        setTrackingEnabled(true);
    }, []);

    const handleToggleMap = () => {
        if (mapOpen) {
            closeMapView();
            return;
        }
        setMapOpen(true);
        setTrackingEnabled(true);
    };

    const handleToggleTrack = () => {
        if (!mapOpen) return;
        if (trackOpen) {
            setTrackOpen(false);
            return;
        }
        setTrackOpen(true);
        setTrackingEnabled(false);
        buildTrackForVehicle(false);
    };

    const handleZoomIn = useCallback((event) => {
        if (event) event.stopPropagation();
        dispatch(zoomIn());
    }, [dispatch]);

    const handleZoomOut = useCallback((event) => {
        if (event) event.stopPropagation();
        dispatch(zoomOut());
    }, [dispatch]);

    const handleToggleMapType = useCallback((event) => {
        if (event) event.stopPropagation();
        if (mapService !== 'google') return;
        const nextType = mapTypeId === 'roadmap' ? 'hybrid' : 'roadmap';
        dispatch(changeGoogleMapTypeId(nextType));
    }, [dispatch, mapService, mapTypeId]);

    const handleToggleZones = useCallback((event) => {
        if (event) event.stopPropagation();
        dispatch(toggleZonePolygons());
    }, [dispatch]);

    const handleToggleMarkers = useCallback((event) => {
        if (event) event.stopPropagation();
        dispatch(togglePlaceMarkers());
    }, [dispatch]);

    const clearAllFilters = () => {
        setSearch('');
        setFilterMode('all');
        setSetupFilter('');
        setIssueFilter('');
        setDeviceTypeFilter('');
        setListenerPortFilter('');
        setTruckVoltageFilter('');
    };

    const hasActiveFilters = Boolean(String(search || '').trim())
        || filterMode !== 'all'
        || Boolean(setupFilter)
        || Boolean(issueFilter)
        || Boolean(deviceTypeFilter)
        || Boolean(listenerPortFilter)
        || Boolean(truckVoltageFilter);

    const handleDeviceTypeFilterClick = (label) => {
        setFilterMode('all');
        setSetupFilter('');
        setIssueFilter('');
        setListenerPortFilter('');
        setTruckVoltageFilter('');
        setDeviceTypeFilter((prev) => (prev === label ? '' : label));
    };

    const handleListenerPortFilterClick = (label) => {
        setFilterMode('all');
        setSetupFilter('');
        setIssueFilter('');
        setDeviceTypeFilter('');
        setTruckVoltageFilter('');
        setListenerPortFilter((prev) => (prev === label ? '' : label));
    };

    const handleTruckVoltageFilterClick = (label) => {
        setFilterMode('all');
        setSetupFilter('');
        setIssueFilter('');
        setDeviceTypeFilter('');
        setListenerPortFilter('');
        setTruckVoltageFilter((prev) => (prev === label ? '' : label));
    };

    const handleFleetSummaryFilter = useCallback((mode) => {
        if (!mode) return;
        setSetupFilter('');
        setIssueFilter('');
        setDeviceTypeFilter('');
        setListenerPortFilter('');
        setTruckVoltageFilter('');
        setFilterMode((prev) => (prev === mode ? 'all' : mode));
    }, []);

    const handleCopyList = async (mode) => {
        const includeImei = mode === 'numbers_imei';
        const dateLabel = moment().format('D MMM YYYY');
        const listType = resolveListTypeLabel();
        const lines = filteredVehicles.map((vehicle) => {
            const name = resolveVehicleName(vehicle);
            if (!includeImei) return name;
            const imei = vehicle?.imei || vehicle?.IMEI || vehicle?.assetId || '';
            return imei ? `${name} - ${imei}` : name;
        });
        const content = [
            `Date - ${dateLabel}`,
            `List Type - ${listType}`,
            ...(lines.length ? lines : ['No vehicles']),
        ].join('\n');

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(content);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = content;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
        } catch (error) {
            // No-op: clipboard could be blocked by browser permissions.
        } finally {
            setCopyAnchorEl(null);
        }
    };

    const handleOpenCopyMenu = (event) => {
        setCopyAnchorEl(event.currentTarget);
    };

    const handleCloseCopyMenu = () => {
        setCopyAnchorEl(null);
    };

    useEffect(() => {
        if (!filteredVehicles.length) {
            setSelectedImei('');
            return;
        }
        const exists = filteredVehicles.some((vehicle) => vehicle?.imei === selectedImei);
        if (!selectedImei || !exists) {
            setSelectedImei(filteredVehicles[0]?.imei || '');
        }
    }, [filteredVehicles, selectedImei]);

    const selectedVehicle = useMemo(() => {
        return filteredVehicles.find((vehicle) => vehicle?.imei === selectedImei) || filteredVehicles[0] || null;
    }, [filteredVehicles, selectedImei]);

    const focusedAssetId = useMemo(() => {
        if (!selectedVehicle) return null;
        const candidate = selectedVehicle?.vehicleDevice?.assetId
            || selectedVehicle?.assetId
            || selectedVehicle?.asset_id
            || selectedVehicle?.imei
            || selectedVehicle?.IMEI
            || '';
        const cleaned = String(candidate || '').trim();
        return cleaned.length ? cleaned : null;
    }, [selectedVehicle]);

    const trackCacheEntry = useMemo(() => {
        if (!focusedAssetId) return null;
        return gensetTrackCache?.[focusedAssetId] || null;
    }, [focusedAssetId, gensetTrackCache]);

    const activeReportId = trackCacheEntry?.reportId || null;
    const activeTrackData = activeReportId ? trackData?.[activeReportId] : null;
    const activeTrackReady = Boolean(activeTrackData?.track && activeTrackData?.dataTable);
    const trackCacheAgeMs = trackCacheEntry?.fetchedAt ? Date.now() - trackCacheEntry.fetchedAt : null;
    const trackCacheStale = trackCacheAgeMs !== null ? trackCacheAgeMs > 30 * 60 * 1000 : true;
    const trackStatusLabel = useMemo(() => {
        if (trackRefreshing) return 'Refreshing track...';
        if (!activeReportId) return 'Track not requested yet.';
        if (activeTrackData?.status === 'nodata') return 'No track data for today.';
        if (activeTrackReady) return 'Track ready.';
        if (activeTrackData?.status === 'ready') return 'Downloading track data...';
        if (activeTrackData?.status === 'submitted') return 'Building track...';
        return 'Track pending...';
    }, [activeReportId, activeTrackData, activeTrackReady, trackRefreshing]);

    const condensedTimelineItems = useMemo(() => {
        if (!activeTrackReady || !activeTrackData) return [];
        const records = Array.isArray(activeTrackData.reportRecords) ? activeTrackData.reportRecords : [];
        const filtered = records.filter((record) => record?.type === 'drive' || record?.type === 'stop');
        if (filtered.length) return filtered;
        const drives = Array.isArray(activeTrackData.drives) ? activeTrackData.drives.map((drive) => ({ ...drive, type: 'drive' })) : [];
        const stops = Array.isArray(activeTrackData.stops) ? activeTrackData.stops.map((stop) => ({ ...stop, type: 'stop' })) : [];
        return [...drives, ...stops].sort((a, b) => (a?.startIndex ?? 0) - (b?.startIndex ?? 0));
    }, [activeTrackData, activeTrackReady]);

    const trackStampIndexPairs = useMemo(() => {
        if (!activeTrackReady || !activeTrackData?.track) return [];
        return activeTrackData.track
            .map((point, index) => {
                const stamp = moment.utc(point.dt_tracker, 'YYYY-MM-DD HH:mm:ss', true).valueOf();
                if (!Number.isFinite(stamp)) return null;
                return { stamp, index };
            })
            .filter(Boolean);
    }, [activeTrackData, activeTrackReady]);

    const trackStamps = useMemo(() => trackStampIndexPairs.map((pair) => pair.stamp), [trackStampIndexPairs]);

    const findClosestTrackIndex = useCallback((targetStamp) => {
        if (!trackStamps.length || !Number.isFinite(targetStamp)) return null;
        let lo = 0;
        let hi = trackStamps.length - 1;
        while (lo < hi) {
            const mid = Math.floor((lo + hi) / 2);
            if (trackStamps[mid] < targetStamp) lo = mid + 1;
            else hi = mid;
        }
        let best = lo;
        if (lo > 0 && Math.abs(trackStamps[lo - 1] - targetStamp) <= Math.abs(trackStamps[lo] - targetStamp)) {
            best = lo - 1;
        }
        return trackStampIndexPairs[best]?.index ?? null;
    }, [trackStampIndexPairs, trackStamps]);

    const handleTimelineItemClick = useCallback((item) => {
        if (!item) return;
        if (item.type === 'drive') {
            const isActive = highlightedDrive?.startIndex === item.startIndex && highlightedDrive?.endIndex === item.endIndex;
            if (isActive) {
                dispatch(removeHighlightedDrive());
            } else {
                dispatch(setHighlightedDrive({ startIndex: item.startIndex, endIndex: item.endIndex }));
            }
            dispatch(removeHighlightedStop());
            dispatch(setNeedBounding(true));
            return;
        }
        if (item.type === 'stop') {
            const isActive = highlightedStop?.startIndex === item.startIndex;
            if (isActive) {
                dispatch(removeHighlightedStop());
            } else {
                dispatch(setHighlightedStop({ startIndex: item.startIndex }));
            }
            dispatch(removeHighlightedDrive());
            dispatch(setNeedBounding(true));
        }
    }, [dispatch, highlightedDrive, highlightedStop]);

    const handleReportSelect = useCallback((reportID) => {
        if (!reportID) return;
        const reportData = reportDataMap?.[reportID];
        if (reportData && reportData.status !== 'submitted' && !reportData.dataTable) {
            dispatch(downloadReport(reportID));
        }
        setSelectedReportId(reportID);
    }, [dispatch, reportDataMap]);

    const handleReportDelete = useCallback((reportID) => {
        if (!reportID) return;
        if (selectedReportId === reportID) {
            setSelectedReportId(null);
        }
        dispatch(deleteReport(reportID));
    }, [dispatch, selectedReportId]);

    const handleOpenReportForm = useCallback(() => {
        setReportFormOpen(true);
    }, []);

    const handleCloseReportForm = useCallback(() => {
        setReportFormOpen(false);
    }, []);

    const handleCloseGetPdfForm = useCallback(() => {
        setGetPdfFormOpen(false);
    }, []);

    const handleRunningPeriodClick = useCallback((period) => {
        if (!period || !activeTrackReady || !activeTrackData?.track) return;
        const startUtc = period?.startTime
            ? moment(period.startTime, 'YYYY-MM-DD HH:mm:ss').subtract(5, 'hours').subtract(30, 'minutes')
            : null;
        const endUtc = period?.endTime
            ? moment(period.endTime, 'YYYY-MM-DD HH:mm:ss').subtract(5, 'hours').subtract(30, 'minutes')
            : null;
        const startIndex = startUtc ? findClosestTrackIndex(startUtc.valueOf()) : null;
        const endIndex = endUtc ? findClosestTrackIndex(endUtc.valueOf()) : startIndex;
        if (startIndex === null || startIndex === undefined) return;
        dispatch(setHighlightedDrive({ startIndex, endIndex: endIndex ?? startIndex }));
        dispatch(removeHighlightedStop());
        dispatch(setNeedBounding(true));
    }, [activeTrackData, activeTrackReady, dispatch, findClosestTrackIndex]);

    const currentRunning = useMemo(() => {
        if (!selectedVehicle) return { running: false, durationMin: 0, startTime: null };
        return getRunningSummary(selectedVehicle);
    }, [selectedVehicle]);

    const activeIssues = useMemo(() => {
        return getCombinedIssues(selectedVehicle, isAdmin);
    }, [selectedVehicle, isAdmin]);

    const setupIssues = useMemo(() => {
        return getSetupIssues(selectedVehicle);
    }, [selectedVehicle]);

    const dashboardDataObject = useMemo(() => ({
        userId,
        isAdmin,
        hasGensetVehicles,
        filters: {
            search,
            filterMode,
            setupFilter,
            issueFilter,
            deviceTypeFilter,
            listenerPortFilter,
            truckVoltageFilter,
        },
        loading: {
            gensetSetupLoading,
            fastHydrationLoading,
            fullHydrationLoading,
            refreshActive,
            liveFlowActive,
        },
        gensetDefaults,
        setupState,
        statusState,
        setupFleet,
        statusFleet,
        hydrationProgress,
        mergedVehicles,
        filteredVehicles,
        selectedImei,
        selectedVehicle,
        currentRunning,
        activeIssues,
        setupIssues,
        dieselData,
        reports: {
            reportIDs,
            reportDataMap,
        },
        track: {
            trackData,
            gensetTrackCache,
            highlightedDrive,
            highlightedStop,
        },
        map: {
            layout,
            mapService,
            mapTypeId,
            gensetViewportPadding,
            showZonePolygons,
            showPlaceMarkers,
        },
    }), [
        userId,
        isAdmin,
        hasGensetVehicles,
        search,
        filterMode,
        setupFilter,
        issueFilter,
        deviceTypeFilter,
        listenerPortFilter,
        truckVoltageFilter,
        gensetSetupLoading,
        fastHydrationLoading,
        fullHydrationLoading,
        refreshActive,
        liveFlowActive,
        gensetDefaults,
        setupState,
        statusState,
        setupFleet,
        statusFleet,
        hydrationProgress,
        mergedVehicles,
        filteredVehicles,
        selectedImei,
        selectedVehicle,
        currentRunning,
        activeIssues,
        setupIssues,
        dieselData,
        reportIDs,
        reportDataMap,
        trackData,
        gensetTrackCache,
        highlightedDrive,
        highlightedStop,
        layout,
        mapService,
        mapTypeId,
        gensetViewportPadding,
        showZonePolygons,
        showPlaceMarkers,
    ]);

    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return;
        // eslint-disable-next-line no-console
        console.log('[GensetDashboard] dashboardDataObject', dashboardDataObject);
    }, [dashboardDataObject]);

    const buildTrackForVehicle = useCallback(async (forceRefresh = false) => {
        if (!focusedAssetId || !selectedVehicle) return;
        if (trackRefreshing) return;
        setTrackRefreshing(true);
        try {
            const cacheEntry = gensetTrackCache?.[focusedAssetId] || null;
            const cachedReportId = cacheEntry?.reportId || null;
            const cachedEntry = cachedReportId ? trackData?.[cachedReportId] : null;
            const cacheAgeMs = cacheEntry?.fetchedAt ? Date.now() - cacheEntry.fetchedAt : null;
            const isStale = cacheAgeMs !== null ? cacheAgeMs > 30 * 60 * 1000 : true;
            let nextReportId = cachedReportId;

            if (forceRefresh || !cachedReportId || isStale || !cachedEntry) {
                if (cachedReportId) {
                    await dispatch(deleteReport(cachedReportId));
                    dispatch(clearGensetTrackCache(focusedAssetId));
                }
                const timeFrom = moment().startOf('day').format('YYYY-MM-DD HH:mm:ss');
                const timeTo = moment().endOf('day').format('YYYY-MM-DD HH:mm:ss');
                const assetName = resolveVehicleName(selectedVehicle);
                const result = await dispatch(historyCustom({
                    timeFrom,
                    timeTo,
                    minStop: 300000,
                    minDrive: 1,
                    assets: [assetName],
                    assetIds: [focusedAssetId],
                }));
                if (historyCustom.fulfilled.match(result)) {
                    const summary = Array.isArray(result.payload) ? result.payload[0] : null;
                    nextReportId = summary?.reportID || null;
                    if (nextReportId) {
                        dispatch(setGensetTrackCache({ imei: focusedAssetId, reportId: nextReportId, fetchedAt: Date.now() }));
                    }
                }
            }

            if (nextReportId) {
                const entry = trackData?.[nextReportId];
                if (entry?.status === 'ready' && !entry?.dataTable) {
                    await dispatch(downloadTrack(nextReportId));
                }
            }
        } finally {
            setTrackRefreshing(false);
        }
    }, [dispatch, focusedAssetId, gensetTrackCache, selectedVehicle, trackData, trackRefreshing]);

    const handleRefreshTrack = useCallback(() => {
        buildTrackForVehicle(true);
    }, [buildTrackForVehicle]);

    useEffect(() => {
        dispatch(disableMapToolbar());
        return () => {
            dispatch(enableMapToolbar());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!mapOpen) {
            dispatch(setMapControl('asset'));
            dispatch(restoreVisibleAssetsMarkers());
            dispatch(setGensetViewportPadding(null));
            dispatch(clearGensetViewportClip());
            dispatch(setGensetTrackReportId(null));
            return;
        }
        dispatch(disableMapToolbar());
        dispatch(setMapControl(trackOpen ? 'track' : 'genset'));
        if (focusedAssetId) {
            dispatch(setVisibleAssetsMarkers([focusedAssetId]));
        }
    }, [dispatch, focusedAssetId, mapOpen, trackOpen]);

    useEffect(() => {
        dispatch(setGensetFocusAssetId(focusedAssetId));
    }, [dispatch, focusedAssetId]);

    useEffect(() => {
        if (!mapOpen || !mapViewportRect || !rootRef.current) {
            dispatch(clearGensetViewportClip());
            return;
        }
        const rootRect = rootRef.current.getBoundingClientRect();
        const viewportLeftAbs = rootRect.left + mapViewportRect.x;
        const viewportTopAbs = rootRect.top + mapViewportRect.y;
        const viewportRightAbs = viewportLeftAbs + mapViewportRect.width;
        const viewportBottomAbs = viewportTopAbs + mapViewportRect.height;

        let mapRect = null;
        if (mapService === 'osm') {
            const node = document.getElementById('mapid');
            mapRect = node?.getBoundingClientRect() || null;
        } else if (mapService === 'google' && window.googlemap?.getDiv) {
            mapRect = window.googlemap.getDiv()?.getBoundingClientRect() || null;
        }
        if (!mapRect) {
            mapRect = mapFrameRect;
        }
        if (!mapRect) return;

        const clipLeft = Math.max(0, viewportLeftAbs - mapRect.left);
        const clipTop = Math.max(0, viewportTopAbs - mapRect.top);
        const clipRight = Math.max(0, mapRect.right - viewportRightAbs);
        const clipBottom = Math.max(0, mapRect.bottom - viewportBottomAbs);
        dispatch(setGensetViewportPadding({
            left: clipLeft + 16,
            top: clipTop + 16,
            right: clipRight + 16,
            bottom: clipBottom + 16,
        }));
        dispatch(setGensetViewportClip({
            left: clipLeft,
            top: clipTop,
            right: clipRight,
            bottom: clipBottom,
            radius: 24,
        }));
    }, [dispatch, mapFrameRect, mapOpen, mapService, mapViewportRect]);

    useEffect(() => {
        if (!mapOpen || !trackOpen) {
            dispatch(setGensetTrackReportId(null));
            return;
        }
        if (activeReportId) {
            dispatch(setGensetTrackReportId(activeReportId));
        }
    }, [dispatch, mapOpen, trackOpen, activeReportId]);

    useEffect(() => {
        if (!mapOpen) return;
        if (!gensetViewportPadding) return;
        dispatch(setNeedBounding(true));
    }, [dispatch, gensetViewportPadding, mapOpen, mapViewportRect, trackOpen, focusedAssetId, activeTrackReady]);

    useEffect(() => {
        if (!trackOpen || !activeReportId || !activeTrackData) return;
        if (activeTrackData?.status === 'ready' && !activeTrackData?.dataTable) {
            dispatch(downloadTrack(activeReportId));
        }
    }, [dispatch, trackOpen, activeReportId, activeTrackData]);

    useEffect(() => {
        if (!mapOpen || !trackingEnabled) return undefined;
        const isInsideViewport = (event) => {
            if (!mapViewportRect || !rootRef.current) return false;
            if (mapControlsRef.current && mapControlsRef.current.contains(event.target)) return false;
            const point = event.touches?.[0] || event.changedTouches?.[0] || event;
            const clientX = point?.clientX;
            const clientY = point?.clientY;
            if (typeof clientX !== 'number' || typeof clientY !== 'number') return false;
            const rootRect = rootRef.current.getBoundingClientRect();
            const left = rootRect.left + mapViewportRect.x;
            const top = rootRect.top + mapViewportRect.y;
            const right = left + mapViewportRect.width;
            const bottom = top + mapViewportRect.height;
            return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
        };

        const handlePointerDown = (event) => {
            if (!mapOpen || !trackingEnabled) return;
            if (!isInsideViewport(event)) return;
            const point = event.touches?.[0] || event.changedTouches?.[0] || event;
            trackingGestureRef.current = {
                active: true,
                startX: point?.clientX || 0,
                startY: point?.clientY || 0,
            };
        };

        const handlePointerMove = (event) => {
            if (!mapOpen || !trackingEnabled) return;
            if (!trackingGestureRef.current.active) return;
            const point = event.touches?.[0] || event.changedTouches?.[0] || event;
            const clientX = point?.clientX;
            const clientY = point?.clientY;
            if (typeof clientX !== 'number' || typeof clientY !== 'number') return;
            const deltaX = Math.abs(clientX - trackingGestureRef.current.startX);
            const deltaY = Math.abs(clientY - trackingGestureRef.current.startY);
            if (deltaX + deltaY > 6) {
                trackingGestureRef.current.active = false;
                setTrackingEnabled(false);
            }
        };

        const handlePointerUp = () => {
            trackingGestureRef.current.active = false;
        };

        const handleWheel = (event) => {
            if (!mapOpen || !trackingEnabled) return;
            if (!isInsideViewport(event)) return;
            setTrackingEnabled(false);
        };

        window.addEventListener('pointerdown', handlePointerDown, true);
        window.addEventListener('pointermove', handlePointerMove, { capture: true, passive: true });
        window.addEventListener('pointerup', handlePointerUp, true);
        window.addEventListener('pointercancel', handlePointerUp, true);
        window.addEventListener('wheel', handleWheel, { capture: true, passive: true });
        window.addEventListener('touchstart', handlePointerDown, { capture: true, passive: true });
        window.addEventListener('touchmove', handlePointerMove, { capture: true, passive: true });
        window.addEventListener('touchend', handlePointerUp, { capture: true, passive: true });
        return () => {
            window.removeEventListener('pointerdown', handlePointerDown, true);
            window.removeEventListener('pointermove', handlePointerMove, { capture: true });
            window.removeEventListener('pointerup', handlePointerUp, true);
            window.removeEventListener('pointercancel', handlePointerUp, true);
            window.removeEventListener('wheel', handleWheel, { capture: true });
            window.removeEventListener('touchstart', handlePointerDown, { capture: true });
            window.removeEventListener('touchmove', handlePointerMove, { capture: true });
            window.removeEventListener('touchend', handlePointerUp, { capture: true });
        };
    }, [mapOpen, mapViewportRect, trackingEnabled]);

    useEffect(() => {
        const node = focusScrollRef.current;
        if (!node) return undefined;
        lastScrollTopRef.current = node.scrollTop;
        return undefined;
    }, [mapOpen, selectedImei]);

    useEffect(() => {
        const node = focusScrollRef.current;
        if (!node) return undefined;
        const handleScroll = () => {
            const currentTop = node.scrollTop;
            if (mapOpen && currentTop < lastScrollTopRef.current - 12) {
                closeMapView();
            }
            lastScrollTopRef.current = currentTop;
        };
        node.addEventListener('scroll', handleScroll, { passive: true });
        return () => node.removeEventListener('scroll', handleScroll);
    }, [closeMapView, mapOpen]);

    useEffect(() => {
        if (prevSelectedImeiRef.current && prevSelectedImeiRef.current !== selectedImei && mapOpen) {
            closeMapView();
        }
        prevSelectedImeiRef.current = selectedImei;
    }, [selectedImei, mapOpen, closeMapView]);

    useEffect(() => {
        if (listTab === 'reports') {
            dispatch(getReportList());
        }
    }, [dispatch, listTab]);

    const runningPeriods = selectedVehicle?.period?.runningPeriods || [];
    const selectedMovingLabel = getMovingLabel(selectedVehicle);
    const selectedAddress = getCurrentAddress(selectedVehicle);
    const kilometersTravelled = selectedVehicle?.kilometersTravelled ?? selectedVehicle?.period?.kilometersTravelled ?? null;
    const metricsLoading = fullHydrationLoading;
    const currentStatusLoading = fullHydrationLoading || gensetSetupLoading;
    const showRunningPeriods = !fullHydrationLoading;
    const showAddress = !fullHydrationLoading && Boolean(selectedAddress);
    const setupLabel = resolveSetupType(selectedVehicle);
    const showSetupLabel = !fullHydrationLoading && !gensetSetupLoading && setupLabel && setupLabel !== 'Unknown';
    const formattedKilometers = useMemo(() => {
        if (kilometersTravelled === null || kilometersTravelled === undefined) return '--';
        const parsed = Number.parseFloat(kilometersTravelled);
        if (!Number.isFinite(parsed)) return String(kilometersTravelled);
        return `${parsed.toFixed(2)} km`;
    }, [kilometersTravelled]);

    const hasStatusData = (statusState?.imeis || []).length > 0;
    const showLoading = fastHydrationLoading && !hasStatusData;
    const showInitialLoading = hasGensetVehicles && gensetSetupLoading && fastHydrationLoading && !hasStatusData;
    const showEmpty = hasGensetVehicles && !showLoading && mergedVehicles.length === 0;

    const lastUpdateStamp = statusState?.generatedAt || statusState?.fetchedAt;
    const lastUpdateLabel = lastUpdateStamp ? moment(lastUpdateStamp).format('h:mm A') : '--';

    const totalRunTimeLabel = formatMinutes(statusFleet?.totalGensetHoursMinSum || 0);
    const estimatedCostLabel = `₹${formatIndianNumber(statusFleet?.estimatedCost || 0)}`;
    const otherIssuesCount = Math.max(0, statusCounts.issues - statusCounts.malfunctionPart - statusCounts.malfunctionFull);
    const batchHydrationActive = Boolean(hydrationProgress?.active);
    const validGensetImeisCount = Number(setupFleet?.validVehiclesCount || 0);
    const fallbackExpectedImeisCount = Number(hydrationProgress?.expectedImeisCount || 0);
    const hydrationTotalImeis = validGensetImeisCount > 0 ? validGensetImeisCount : fallbackExpectedImeisCount;
    const hydrationDoneImeis = hydrationTotalImeis > 0
        ? Math.min(Number(hydrationProgress?.hydratedImeisCount || 0), hydrationTotalImeis)
        : 0;
    const hydrationProgressLabel = hydrationTotalImeis > 0
        ? `${hydrationDoneImeis} / ${hydrationTotalImeis}`
        : null;
    const hydrationProgressPercent = hydrationTotalImeis > 0
        ? Math.round((hydrationDoneImeis / hydrationTotalImeis) * 100)
        : null;
    const fleetSummaryTitle = batchHydrationActive && hydrationProgressPercent !== null
        ? `Fleet Summary (${hydrationProgressPercent}%)`
        : 'Fleet Summary';

    const fleetSummaryPrimary = [
        {
            key: 'running',
            label: 'Running Now',
            value: statusCounts.running,
            icon: BoltIcon,
            color: palette.emerald,
            filterMode: 'running',
        },
        {
            key: 'active-today',
            label: 'Active Today',
            value: statusCounts.todayActive,
            icon: ScheduleIcon,
            color: palette.accentDark,
            filterMode: 'todayActive',
        },
        {
            key: 'total-run',
            label: 'Total Run Time',
            value: totalRunTimeLabel,
            icon: ScheduleIcon,
            color: palette.accentDark,
        },
        {
            key: 'estimated-cost',
            label: 'Estimated Cost',
            value: estimatedCostLabel,
            icon: LocalGasStationIcon,
            color: palette.emerald,
        },
    ];

    const fleetSummaryIssues = [
        {
            key: 'overused',
            label: 'Overused',
            value: statusCounts.overused,
            icon: ScheduleIcon,
            color: palette.amber,
            filterMode: 'overused',
        },
        {
            key: 'malfunction-part',
            label: 'Part Malfunction',
            value: statusCounts.malfunctionPart,
            icon: WarningAmberIcon,
            color: palette.crimson,
            filterMode: 'malfunctionPart',
        },
        {
            key: 'malfunction-full',
            label: 'Full Malfunction',
            value: statusCounts.malfunctionFull,
            icon: WarningAmberIcon,
            color: palette.crimson,
            filterMode: 'malfunctionFull',
        },
        {
            key: 'other-issue',
            label: 'Other Issue',
            value: otherIssuesCount,
            icon: WarningAmberIcon,
            color: palette.crimson,
            filterMode: 'otherIssue',
        },
    ];

    const appBarHeight = 48;
    const layoutMain = layout?.main || {};
    const dashboardBackground = `linear-gradient(160deg, ${palette.soft} 0%, #ffffff 100%)`;

    const measureViewport = useCallback(() => {
        if (!rootRef.current || !mapViewportRef.current) return;
        const rootRect = rootRef.current.getBoundingClientRect();
        const mapRect = mapViewportRef.current.getBoundingClientRect();
        setMapViewportRect({
            x: mapRect.left - rootRect.left,
            y: mapRect.top - rootRect.top,
            width: mapRect.width,
            height: mapRect.height,
        });
    }, []);

    useEffect(() => {
        if (!mapOpen) {
            setMapViewportRect(null);
            return;
        }
        measureViewport();
    }, [mapOpen, trackOpen, selectedImei, measureViewport]);

    useEffect(() => {
        if (!mapOpen) return undefined;
        const handleResize = () => measureViewport();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [mapOpen, measureViewport]);

    useEffect(() => {
        const node = focusScrollRef.current;
        if (!mapOpen || !node) return undefined;
        const handleScroll = () => measureViewport();
        node.addEventListener('scroll', handleScroll, { passive: true });
        return () => node.removeEventListener('scroll', handleScroll);
    }, [mapOpen, measureViewport]);

    const mapControlButtonBase = {
        width: 30,
        height: 30,
        borderRadius: 10,
        border: `1px solid ${palette.border}`,
        backgroundColor: '#ffffff',
        color: palette.muted,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#ffffff',
            color: palette.ink,
        },
    };

    const mapControlButtonSx = (active = false) => ({
        ...mapControlButtonBase,
        ...(active
            ? {
                backgroundColor: `${palette.accent}22`,
                borderColor: palette.accent,
                color: palette.accentDark,
                boxShadow: `0 8px 18px ${palette.accentGlow}`,
            }
            : {}),
    });

    return (
        <>
            <Box
            ref={rootRef}
            sx={{
                ...layoutMain,
                position: 'fixed',
                top: `${appBarHeight}px`,
                height: `calc(100vh - ${appBarHeight}px)`,
                left: layoutMain?.left ?? 0,
                width: layoutMain?.width ?? '100%',
                overflow: 'hidden',
                pointerEvents: mapOpen ? 'none' : 'auto',
                background: mapOpen ? 'transparent' : dashboardBackground,
                p: { xs: 1, md: 1.5 },
                pt: { xs: 0.5, md: 0.5 },
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {mapOpen && mapViewportRect && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 0,
                        pointerEvents: 'none',
                    }}
                >
                    <svg width="100%" height="100%" style={{ display: 'block' }}>
                        <defs>
                            <linearGradient id="dashboardBg" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor={palette.soft} />
                                <stop offset="100%" stopColor="#ffffff" />
                            </linearGradient>
                            <mask id="dashboardMask">
                                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                                <rect
                                    x={mapViewportRect.x}
                                    y={mapViewportRect.y}
                                    width={mapViewportRect.width}
                                    height={mapViewportRect.height}
                                    rx="24"
                                    ry="24"
                                    fill="black"
                                />
                            </mask>
                        </defs>
                        <rect x="0" y="0" width="100%" height="100%" fill="url(#dashboardBg)" mask="url(#dashboardMask)" />
                    </svg>
                </Box>
            )}

            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, pointerEvents: mapOpen ? 'none' : 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, marginBottom: 1.5, pt: 0.5, pb: 0.5, pointerEvents: 'auto' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: palette.ink }}>Genset Command Center</Typography>
                    <Box
                        sx={{
                            padding: '8px 12px',
                            borderRadius: 1.5,
                            border: `1px solid ${palette.border}`,
                            background: palette.card,
                            minWidth: 220,
                            boxShadow: '0 6px 16px rgba(16, 42, 67, 0.08)',
                            display: 'grid',
                            gridTemplateColumns: 'auto auto',
                            rowGap: 0.4,
                            columnGap: 1.2,
                            alignItems: 'center',
                            justifyItems: 'end',
                            textAlign: 'right',
                        }}
                    >
                        <Typography variant="caption" sx={{ color: palette.muted, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>
                            Dashboard For
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: palette.ink, fontWeight: 700 }}>
                            {moment().format('D MMM YYYY')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: palette.muted, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>
                            Last Update
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: palette.ink, fontWeight: 700 }}>
                            {lastUpdateLabel}
                        </Typography>
                    </Box>
                </Box>

                {showInitialLoading ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                padding: 3,
                                borderRadius: 3,
                                border: `1px solid ${palette.border}`,
                                background: `radial-gradient(circle at top, ${palette.accentSoft}, #ffffff)`,
                                boxShadow: '0 18px 40px rgba(16, 42, 67, 0.12)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <CircularProgress size={68} thickness={4} sx={{ color: palette.accentDark }} />
                            <Typography variant="subtitle2" sx={{ color: palette.muted, fontWeight: 600 }}>
                                Loading genset intelligence...
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <>
                        {showLoading && (
                            <LinearProgress sx={{ marginBottom: 2, backgroundColor: palette.accentSoft, '& .MuiLinearProgress-bar': { backgroundColor: palette.accent } }} />
                        )}

                        {!hasGensetVehicles && (
                            <Paper sx={{ padding: 3, borderRadius: 3, border: `1px solid ${palette.border}` }}>
                                <Typography variant="h6" sx={{ color: palette.ink, marginBottom: 1 }}>No genset sensors detected</Typography>
                                <Typography variant="body2" sx={{ color: palette.muted }}>
                                    This account does not have any generator-enabled vehicles configured yet.
                                </Typography>
                            </Paper>
                        )}

                        {hasGensetVehicles && (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', lg: '300px 320px 1fr' },
                                    gridTemplateAreas: { xs: `"summary" "list" "focus"`, lg: `"summary list focus"` },
                                    gap: '13px',
                                    flex: 1,
                                    minHeight: 0,
                                }}
                            >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', minHeight: 0, overflow: 'hidden', paddingRight: 0.5, gridArea: 'list', pointerEvents: 'auto' }}>
                        <Paper sx={{ padding: 2, borderRadius: 3, border: `1px solid ${palette.border}`, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            <Stack spacing={1.2}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={1.6} alignItems="center">
                                        {[
                                            { key: 'vehicles', label: `Vehicles (${filteredVehicles.length})` },
                                            { key: 'reports', label: 'Gen Reports' },
                                        ].map((tab) => {
                                            const isActive = listTab === tab.key;
                                            return (
                                                <Typography
                                                    key={tab.key}
                                                    variant="subtitle1"
                                                    onClick={() => setListTab(tab.key)}
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: isActive ? palette.ink : palette.muted,
                                                        cursor: 'pointer',
                                                        borderBottom: `2px solid ${isActive ? palette.accent : 'transparent'}`,
                                                        paddingBottom: 0.2,
                                                        transition: 'color 0.2s ease, border-color 0.2s ease',
                                                    }}
                                                >
                                                    {tab.label}
                                                </Typography>
                                            );
                                        })}
                                    </Stack>
                                    {listTab === 'vehicles' && isAdmin && (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Tooltip title="Copy List">
                                                <IconButton size="small" onClick={handleOpenCopyMenu}>
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Menu
                                                anchorEl={copyAnchorEl}
                                                open={Boolean(copyAnchorEl)}
                                                onClose={handleCloseCopyMenu}
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                            >
                                                <MenuItem onClick={() => handleCopyList('numbers')}>Vehicle Numbers</MenuItem>
                                                <MenuItem onClick={() => handleCopyList('numbers_imei')}>Vehicle with IMEI</MenuItem>
                                            </Menu>
                                        </Stack>
                                    )}
                                </Stack>
                                {listTab === 'vehicles' ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <TextField
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            placeholder="Search"
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Select
                                            value={filterMode}
                                            onChange={(event) => {
                                                setFilterMode(event.target.value);
                                                setSetupFilter('');
                                                setIssueFilter('');
                                                setDeviceTypeFilter('');
                                                setListenerPortFilter('');
                                                setTruckVoltageFilter('');
                                            }}
                                            size="small"
                                            sx={{
                                                minWidth: 140,
                                                '& .MuiSelect-select': {
                                                    fontSize: 11,
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        '& .MuiMenuItem-root': {
                                                            fontSize: 11,
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">All ({statusCounts.all})</MenuItem>
                                            <MenuItem value="running">Running ({statusCounts.running})</MenuItem>
                                            <MenuItem value="off">Off ({statusCounts.off})</MenuItem>
                                            <MenuItem value="todayActive">Today Active ({statusCounts.todayActive})</MenuItem>
                                            <MenuItem value="moving">Moving ({statusCounts.moving})</MenuItem>
                                            <MenuItem value="stationary">Stationary ({statusCounts.stationary})</MenuItem>
                                            <MenuItem value="overused">Overused ({statusCounts.overused})</MenuItem>
                                            <MenuItem value="malfunctionPart">Part Malfunction ({statusCounts.malfunctionPart})</MenuItem>
                                            <MenuItem value="malfunctionFull">Full Malfunction ({statusCounts.malfunctionFull})</MenuItem>
                                            <MenuItem value="issues">Issues ({statusCounts.issues})</MenuItem>
                                        </Select>
                                        {hasActiveFilters && (
                                            <Tooltip title="Clear filters">
                                                <IconButton size="small" onClick={clearAllFilters}>
                                                    <FilterAltOffIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={handleOpenReportForm}
                                            sx={{ textTransform: 'none', fontWeight: 600 }}
                                        >
                                            Create Report
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>
                            <Box sx={{ marginTop: 1.2, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 0.5 }}>
                                {listTab === 'vehicles' && (
                                    <>
                                        {showEmpty && (
                                            <Typography variant="body2" sx={{ color: palette.muted }}>No genset vehicles returned from API.</Typography>
                                        )}
                                        {!showEmpty && filteredVehicles.length === 0 && (
                                            <Typography variant="body2" sx={{ color: palette.muted }}>No matches for the current filter.</Typography>
                                        )}
                                        {filteredVehicles.map((vehicle) => {
                                            const runningSummary = getRunningSummary(vehicle);
                                            const runningBadge = getListRunningBadge(vehicle, runningSummary);
                                            const runningSensorLabel = getRunningSensorLabel(vehicle);
                                            const cutoffInfo = !runningBadge.active ? getCutoffLabel(vehicle) : null;
                                            const cutoffSeverity = cutoffInfo?.severity;
                                            const highlightColor = cutoffSeverity === 'critical'
                                                ? palette.crimson
                                                : cutoffSeverity === 'warn'
                                                    ? palette.amber
                                                    : palette.accent;
                                            const highlightBg = cutoffSeverity === 'critical'
                                                ? 'rgba(193, 18, 31, 0.08)'
                                                : cutoffSeverity === 'warn'
                                                    ? 'rgba(245, 158, 11, 0.08)'
                                                    : palette.accentSoft;
                                            const movingLabel = getMovingLabel(vehicle);
                                            const addressLabel = getCurrentAddress(vehicle);
                                            const selected = vehicle?.imei === selectedImei;
                                            return (
                                                <Box
                                                    key={vehicle?.imei}
                                                    onClick={() => setSelectedImei(vehicle?.imei || '')}
                                                    sx={{
                                                        borderRadius: 2,
                                                        border: `1px solid ${selected ? highlightColor : palette.border}`,
                                                        padding: 1.5,
                                                        cursor: 'pointer',
                                                        background: selected ? highlightBg : palette.card,
                                                        boxShadow: selected ? `0 6px 18px ${highlightColor}33` : 'none',
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                >
                                                    <Stack spacing={0.8}>
                                                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: palette.ink }}>
                                                                {resolveVehicleName(vehicle)}
                                                            </Typography>
                                                            <Chip
                                                                label={runningBadge.label}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: runningBadge.bg,
                                                                    color: runningBadge.color,
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                        </Stack>
                                                        {runningSummary.running && (
                                                            <Typography variant="caption" sx={{ color: palette.muted }}>
                                                                {runningSummary.inferred
                                                                    ? `Running now, Sensor - ${runningSensorLabel || 'Not Monitored'}`
                                                                    : `Running since ${formatDateTime(runningSummary.startTime)} - ${formatDurationVerbose(runningSummary.durationMin)}, Sensor - ${runningSensorLabel || 'Not Monitored'}`}
                                                            </Typography>
                                                        )}
                                                        {!runningSummary.running && cutoffInfo && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: cutoffSeverity === 'critical' ? palette.crimson : palette.amber,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {cutoffInfo.label}
                                                            </Typography>
                                                        )}
                                                        {(movingLabel || addressLabel) && (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.6,
                                                                    color: palette.muted,
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                }}
                                                            >
                                                                <MovingIndicator moving={movingLabel === 'Moving'} />
                                                                {addressLabel && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: palette.muted,
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                        }}
                                                                    >
                                                                        {addressLabel}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </Box>
                                            );
                                        })}
                                    </>
                                )}
                                {listTab === 'reports' && (
                                    <>
                                        {genfleetReportIds.length === 0 && (
                                            <Box
                                                sx={{
                                                    borderRadius: 2,
                                                    border: `1px dashed ${palette.border}`,
                                                    padding: 2,
                                                    background: palette.soft,
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ color: palette.muted }}>
                                                    No Generator Fleet reports yet.
                                                </Typography>
                                            </Box>
                                        )}
                                        {genfleetReportIds.map((reportID) => {
                                            const report = reportDataMap?.[reportID];
                                            if (!report) return null;
                                            const isSelected = selectedReportId === reportID;
                                            const status = report?.status || 'ready';
                                            const statusLabel = status === 'submitted'
                                                ? 'Building'
                                                : status === 'nodata'
                                                    ? 'No Data'
                                                    : 'Ready';
                                            const statusColor = status === 'submitted'
                                                ? palette.amber
                                                : status === 'nodata'
                                                    ? palette.crimson
                                                    : palette.emerald;
                                            const timeFrom = report?.timeFrom || report?.reqBody?.timeFrom || null;
                                            const timeTo = report?.timeTo || report?.reqBody?.timeTo || null;
                                            const assetName = report?.assetName || report?.reqBody?.assetName || `${report?.reqBody?.assetIds?.length || 0} Vehicles`;
                                            return (
                                                <Box
                                                    key={reportID}
                                                    onClick={() => handleReportSelect(reportID)}
                                                    sx={{
                                                        borderRadius: 2,
                                                        border: `1px solid ${isSelected ? palette.accent : palette.border}`,
                                                        padding: 1.2,
                                                        cursor: 'pointer',
                                                        background: isSelected ? palette.accentSoft : palette.card,
                                                        boxShadow: isSelected ? `0 6px 16px ${palette.accentGlow}` : 'none',
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                >
                                                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.ink }}>
                                                            {report?.reportName || 'Generator Fleet'}
                                                        </Typography>
                                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                                            <Chip
                                                                label={statusLabel}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: `${statusColor}22`,
                                                                    color: statusColor,
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                            <IconButton size="small">
                                                                <Icon path={mdiEye} size={0.7} color={palette.muted} />
                                                            </IconButton>
                                                        </Stack>
                                                    </Stack>
                                                    <Typography variant="caption" sx={{ color: palette.muted, display: 'block' }}>
                                                        {assetName}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: palette.muted }}>
                                                        {timeFrom ? moment(timeFrom).format('D MMM hh:mm A') : '--'} → {timeTo ? moment(timeTo).format('D MMM hh:mm A') : '--'}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </>
                                )}
                            </Box>
                        </Paper>
                    </Box>

                    <Box
                        ref={focusScrollRef}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            height: '100%',
                            overflowY: 'auto',
                            paddingRight: 0.5,
                            gridArea: 'focus',
                            pointerEvents: mapOpen && listTab !== 'reports' ? 'none' : 'auto',
                        }}
                    >
                        <Paper
                            sx={{
                                padding: 2.5,
                                borderRadius: 3,
                                border: `1px solid ${palette.border}`,
                                background: 'transparent',
                                boxShadow: 'none',
                                pointerEvents: mapOpen && listTab !== 'reports' ? 'none' : 'auto',
                            }}
                        >
                            {listTab === 'reports' && (
                                <>
                                    {!selectedReportData && (
                                        <Typography variant="body1" sx={{ color: palette.muted }}>
                                            Select a report to see details.
                                        </Typography>
                                    )}
                                    {selectedReportData && (
                                        <Stack spacing={2}>
                                            <Box
                                                sx={{
                                                    padding: 1.5,
                                                    borderRadius: 2,
                                                    border: `1px solid ${palette.border}`,
                                                    background: palette.card,
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: palette.ink }}>
                                                            {selectedReportData?.reportName || 'Generator Fleet'} Report
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: palette.muted }}>
                                                            {selectedReportData?.assetName || 'Vehicles'}
                                                        </Typography>
                                                    </Box>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        {selectedReportData?.status && (
                                                            <Chip
                                                                size="small"
                                                                label={selectedReportData.status === 'submitted' ? 'Building' : selectedReportData.status === 'nodata' ? 'No Data' : 'Ready'}
                                                                sx={{
                                                                    backgroundColor: selectedReportData.status === 'submitted'
                                                                        ? `${palette.amber}22`
                                                                        : selectedReportData.status === 'nodata'
                                                                            ? `${palette.crimson}22`
                                                                            : `${palette.emerald}22`,
                                                                    color: selectedReportData.status === 'submitted'
                                                                        ? palette.amber
                                                                        : selectedReportData.status === 'nodata'
                                                                            ? palette.crimson
                                                                            : palette.emerald,
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                        )}
                                                        <Tooltip title="Download Excel">
                                                            <IconButton size="small" onClick={() => dispatch(getExcel(selectedReportId))}>
                                                                <Icon path={mdiMicrosoftExcel} size={0.75} color={palette.emerald} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Download PDF">
                                                            <IconButton size="small" onClick={() => dispatch(getPdf(selectedReportId))}>
                                                                <Icon path={mdiFilePdfBox} size={0.75} color={palette.crimson} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Email PDF">
                                                            <IconButton size="small" onClick={() => setGetPdfFormOpen(true)}>
                                                                <Icon path={mdiEmailArrowRight} size={0.75} color={palette.accentDark} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete Report">
                                                            <IconButton size="small" onClick={() => handleReportDelete(selectedReportId)}>
                                                                <Icon path={mdiDelete} size={0.75} color={palette.crimson} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                            {selectedReportData?.dataTable ? (
                                                <GeneratorFleetCompact reportData={selectedReportData} />
                                            ) : (
                                                <Typography variant="body2" sx={{ color: palette.muted }}>
                                                    Report data is still loading. Please wait.
                                                </Typography>
                                            )}
                                            <GetPdfForm
                                                getPdfFormOpen={getPdfFormOpen}
                                                handleGetPdfFormClose={handleCloseGetPdfForm}
                                                reportIDs={[selectedReportId]}
                                            />
                                        </Stack>
                                    )}
                                </>
                            )}
                            {listTab !== 'reports' && !selectedVehicle && (
                                <Typography variant="body1" sx={{ color: palette.muted }}>
                                    Select a vehicle to see detailed generator intelligence.
                                </Typography>
                            )}
                            {listTab !== 'reports' && selectedVehicle && (
                                <Stack spacing={2.5}>
                                    <Box
                                        sx={{
                                            padding: 1.5,
                                            borderRadius: 2,
                                            border: `1px solid ${palette.border}`,
                                            background: palette.card,
                                            pointerEvents: 'auto',
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            spacing={1}
                                            sx={{ pointerEvents: 'auto' }}
                                        >
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: palette.ink }}>
                                                {resolveVehicleName(selectedVehicle)}
                                            </Typography>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                {selectedVehicle?.overused && (
                                                    <Chip
                                                        icon={<BoltIcon />}
                                                        label="Overuse"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: `${palette.amber}22`,
                                                            color: palette.amber,
                                                            fontWeight: 600,
                                                            '& .MuiChip-icon': { color: palette.amber },
                                                        }}
                                                    />
                                                )}
                                                {isTamperSuspected(selectedVehicle) && (
                                                    <Chip
                                                        icon={<WarningAmberIcon />}
                                                        label="Tampering"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: `${palette.crimson}22`,
                                                            color: palette.crimson,
                                                            fontWeight: 600,
                                                            '& .MuiChip-icon': { color: palette.crimson },
                                                        }}
                                                    />
                                                )}
                                                <Button
                                                    size="small"
                                                    variant={mapOpen ? 'contained' : 'outlined'}
                                                    startIcon={<MapIcon />}
                                                    onClick={handleToggleMap}
                                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                                >
                                                    {mapOpen ? 'Hide Map' : 'Show Map'}
                                                </Button>
                                                {mapOpen && (
                                                    <Button
                                                        size="small"
                                                        variant={trackOpen ? 'contained' : 'outlined'}
                                                        startIcon={<TimelineIcon />}
                                                        onClick={handleToggleTrack}
                                                        sx={{ textTransform: 'none', fontWeight: 600 }}
                                                    >
                                                        {trackOpen ? 'Hide Track' : 'Show Track'}
                                                    </Button>
                                                )}
                                            </Stack>
                                        </Stack>
                                        {showSetupLabel && (
                                            <Typography variant="subtitle2" sx={{ color: palette.muted }}>
                                                Setup: {setupLabel}
                                            </Typography>
                                        )}
                                        {(selectedMovingLabel || showAddress) && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                sx={{
                                                    marginTop: 0.5,
                                                    justifyContent: 'space-between',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                                                    {selectedMovingLabel && (
                                                        <Chip
                                                            icon={<MovingIndicator moving={selectedMovingLabel === 'Moving'} />}
                                                            label={selectedMovingLabel}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: selectedMovingLabel === 'Moving' ? `${palette.emerald}22` : `${palette.amber}22`,
                                                                color: selectedMovingLabel === 'Moving' ? palette.emerald : palette.amber,
                                                                fontWeight: 600,
                                                                '& .MuiChip-icon': {
                                                                    marginLeft: '6px',
                                                                    marginRight: '-2px',
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                    {showAddress && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: palette.muted,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            {selectedAddress}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                                {mapOpen && (
                                                    <Stack
                                                        ref={mapControlsRef}
                                                        direction="row"
                                                        spacing={0.6}
                                                        alignItems="center"
                                                        sx={{ flexShrink: 0 }}
                                                    >
                                                        <Tooltip title="Zoom in">
                                                            <IconButton size="small" onClick={handleZoomIn} sx={mapControlButtonSx(false)}>
                                                                <ZoomInIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Zoom out">
                                                            <IconButton size="small" onClick={handleZoomOut} sx={mapControlButtonSx(false)}>
                                                                <ZoomOutIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={mapService === 'google' ? 'Toggle map type' : 'Map type (Google only)'}>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={handleToggleMapType}
                                                                    sx={mapControlButtonSx(mapService === 'google' && mapTypeId !== 'roadmap')}
                                                                    disabled={mapService !== 'google'}
                                                                >
                                                                    <LayersIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Show/Hide Zones">
                                                            <IconButton size="small" onClick={handleToggleZones} sx={mapControlButtonSx(showZonePolygons)}>
                                                                <MapIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Show/Hide Markers">
                                                            <IconButton size="small" onClick={handleToggleMarkers} sx={mapControlButtonSx(showPlaceMarkers)}>
                                                                <PlaceIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        )}
                                    </Box>

                                    {mapOpen && (
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: trackOpen ? { xs: '1fr', md: '2fr 1fr' } : '1fr',
                                                gap: 1.5,
                                                alignItems: 'stretch',
                                                pointerEvents: 'none',
                                            }}
                                        >
                                            {/** Keep map + timeline panels equal height across zoom/viewport sizes */}
                                            {(() => {
                                                const panelHeight = { xs: 260, md: 420 };
                                                return (
                                                    <>
                                            <Box
                                                ref={mapViewportRef}
                                                sx={{
                                                    position: 'relative',
                                                    borderRadius: 3,
                                                    border: `1px solid ${palette.border}`,
                                                    background: 'transparent',
                                                    minHeight: 260,
                                                    height: panelHeight,
                                                    maxHeight: '50vh',
                                                    overflow: 'hidden',
                                                    pointerEvents: 'none',
                                                    zIndex: 25,
                                                    boxShadow: '0 10px 24px rgba(16, 42, 67, 0.08)',
                                                }}
                                            >
                                            </Box>
                                            {trackOpen && (
                                                <Paper
                                                    sx={{
                                                        padding: 1.5,
                                                        borderRadius: 2,
                                                        border: `1px solid ${palette.border}`,
                                                        background: palette.card,
                                                        minHeight: 260,
                                                        height: panelHeight,
                                                        maxHeight: '50vh',
                                                        pointerEvents: 'auto',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        spacing={1}
                                                        sx={{ marginBottom: 1, flexWrap: 'nowrap' }}
                                                    >
                                                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                                                            {[
                                                                { key: 'timeline', label: 'Timeline' },
                                                                { key: 'activity', label: 'Gen Activity' },
                                                            ].map((tab) => {
                                                                const isActive = timelineTab === tab.key;
                                                                return (
                                                                    <Typography
                                                                        key={tab.key}
                                                                        variant="subtitle2"
                                                                        onClick={() => setTimelineTab(tab.key)}
                                                                        sx={{
                                                                            fontWeight: 700,
                                                                            color: isActive ? palette.ink : palette.muted,
                                                                            cursor: 'pointer',
                                                                            whiteSpace: 'nowrap',
                                                                            borderBottom: `2px solid ${isActive ? palette.accent : 'transparent'}`,
                                                                            paddingBottom: 0.2,
                                                                            transition: 'color 0.2s ease, border-color 0.2s ease',
                                                                        }}
                                                                    >
                                                                        {tab.label}
                                                                    </Typography>
                                                                );
                                                            })}
                                                        </Stack>
                                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                                                            {activeTrackReady && activeReportId && (
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={() => history.push('/secured/trackview', { reportID: activeReportId })}
                                                                    sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                                                                >
                                                                    More Details
                                                                </Button>
                                                            )}
                                                            <Tooltip title="Refresh Track">
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={handleRefreshTrack}
                                                                        disabled={trackRefreshing || !focusedAssetId}
                                                                        sx={{
                                                                            border: `1px solid ${palette.border}`,
                                                                            borderRadius: 1.5,
                                                                            backgroundColor: '#fff',
                                                                            boxShadow: '0 6px 14px rgba(16, 42, 67, 0.08)',
                                                                        }}
                                                                    >
                                                                        <RefreshIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </Stack>
                                                    </Stack>
                                                    {trackRefreshing && (
                                                        <LinearProgress
                                                            sx={{
                                                                marginBottom: 1,
                                                                backgroundColor: palette.accentSoft,
                                                                '& .MuiLinearProgress-bar': { backgroundColor: palette.accent },
                                                            }}
                                                        />
                                                    )}
                                                    {trackCacheStale && activeReportId && !trackRefreshing && (
                                                        <Typography variant="caption" sx={{ color: palette.amber }}>
                                                            Track cache is older than 30 minutes. Refresh recommended.
                                                        </Typography>
                                                    )}
                                                    {timelineTab === 'timeline' && (
                                                        activeTrackReady ? (
                                                            <Stack
                                                                spacing={0.8}
                                                                sx={{
                                                                    marginTop: 1.2,
                                                                    flex: 1,
                                                                    minHeight: 0,
                                                                    overflowY: 'auto',
                                                                    paddingRight: 0.5,
                                                                }}
                                                            >
                                                                {condensedTimelineItems.length === 0 && (
                                                                    <Typography variant="caption" sx={{ color: palette.muted }}>
                                                                        No drive/stop records yet.
                                                                    </Typography>
                                                                )}
                                                                {condensedTimelineItems.map((item, index) => {
                                                                    const isDrive = item.type === 'drive';
                                                                    const isActive = isDrive
                                                                        ? highlightedDrive?.startIndex === item.startIndex && highlightedDrive?.endIndex === item.endIndex
                                                                        : highlightedStop?.startIndex === item.startIndex;
                                                                    const startTime = isDrive ? item.driveStartTime : item.stopStartTime;
                                                                    const endTime = isDrive ? item.driveEndTime : item.stopEndTime;
                                                                    const oneDay = startTime && endTime
                                                                        ? moment(startTime).format('D/M/Y') === moment(endTime).format('D/M/Y')
                                                                        : true;
                                                                    const timeLabel = startTime && endTime
                                                                        ? `${moment(startTime).format(oneDay ? 'h:mm A' : 'D MMM h:mm A')} → ${moment(endTime).format(oneDay ? 'h:mm A' : 'D MMM h:mm A')}`
                                                                        : '--';
                                                                    const distanceLabel = isDrive && item.driveDistance !== undefined
                                                                        ? `${Number(item.driveDistance).toLocaleString('en-IN', { maximumFractionDigits: 1 })} km`
                                                                        : null;
                                                                    const durationLabel = isDrive ? item.driveDuration : item.stopDuration;
                                                                    const address = !isDrive && activeTrackData?.track?.[item.startIndex]?.address
                                                                        ? (activeTrackData.track[item.startIndex].address.net_addr
                                                                            || activeTrackData.track[item.startIndex].address.fmtd_addr)
                                                                        : null;
                                                                    const rightLabel = isDrive ? (distanceLabel || '--') : (durationLabel || '--');
                                                                    return (
                                                                        <Box
                                                                            key={`${item.type}-${item.startIndex}-${index}`}
                                                                            onClick={() => handleTimelineItemClick(item)}
                                                                            sx={{
                                                                                display: 'grid',
                                                                                gridTemplateColumns: 'auto 1fr auto',
                                                                                gap: 1,
                                                                                alignItems: 'center',
                                                                                padding: 1,
                                                                                borderRadius: 1.5,
                                                                                border: `1px solid ${isActive ? palette.accent : palette.border}`,
                                                                                background: isActive ? palette.accentSoft : palette.card,
                                                                                cursor: 'pointer',
                                                                                transition: 'all 0.2s ease',
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    width: 34,
                                                                                    height: 34,
                                                                                    borderRadius: '50%',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    backgroundColor: isDrive ? `${palette.emerald}22` : `${palette.amber}22`,
                                                                                    color: isDrive ? palette.emerald : palette.amber,
                                                                                }}
                                                                            >
                                                                                {isDrive ? (
                                                                                    <Icon path={mdiSteering} size={0.75} color={palette.emerald} />
                                                                                ) : (
                                                                                    <Typography variant="caption" sx={{ fontWeight: 800, color: palette.amber }}>
                                                                                        P
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                            <Box sx={{ minWidth: 0 }}>
                                                                                <Typography variant="caption" sx={{ color: palette.muted, fontWeight: 600 }}>
                                                                                    {timeLabel}
                                                                                </Typography>
                                                                                {!isDrive && (
                                                                                    <Tooltip title={address || ''}>
                                                                                        <Typography
                                                                                            variant="caption"
                                                                                            sx={{
                                                                                                color: palette.ink,
                                                                                                display: 'block',
                                                                                                whiteSpace: 'nowrap',
                                                                                                overflow: 'hidden',
                                                                                                textOverflow: 'ellipsis',
                                                                                            }}
                                                                                        >
                                                                                            {address || 'Address not available'}
                                                                                        </Typography>
                                                                                    </Tooltip>
                                                                                )}
                                                                            </Box>
                                                                            <Typography variant="caption" sx={{ color: palette.muted, fontWeight: 700 }}>
                                                                                {rightLabel}
                                                                            </Typography>
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Stack>
                                                        ) : (
                                                            <Typography variant="caption" sx={{ color: palette.muted, marginTop: 1.2 }}>
                                                                Track data not available yet.
                                                            </Typography>
                                                        )
                                                    )}
                                                    {timelineTab === 'activity' && (
                                                        <Stack
                                                            spacing={0.8}
                                                            sx={{
                                                                marginTop: 1.2,
                                                                flex: 1,
                                                                minHeight: 0,
                                                                overflowY: 'auto',
                                                                paddingRight: 0.5,
                                                            }}
                                                        >
                                                            {runningPeriods.length === 0 && (
                                                                <Typography variant="caption" sx={{ color: palette.muted }}>
                                                                    No generator activity yet.
                                                                </Typography>
                                                            )}
                                                            {runningPeriods.map((period, index) => {
                                                                const startTime = period?.startTime || null;
                                                                const endTime = period?.endTime || null;
                                                                const oneDay = startTime && endTime
                                                                    ? moment(startTime).format('D/M/Y') === moment(endTime).format('D/M/Y')
                                                                    : true;
                                                                const timeLabel = startTime && endTime
                                                                    ? `${moment(startTime).format(oneDay ? 'h:mm A' : 'D MMM h:mm A')} → ${moment(endTime).format(oneDay ? 'h:mm A' : 'D MMM h:mm A')}`
                                                                    : formatDateTime(startTime);
                                                                const address = period?.startAddress?.net_addr
                                                                    || period?.startAddress?.fmtd_addr
                                                                    || period?.endAddress?.net_addr
                                                                    || period?.endAddress?.fmtd_addr
                                                                    || null;
                                                                const durationMinutes = period?.durationMinutes ?? period?.durationMin ?? period?.duration ?? 0;
                                                                const sideLabel = period?.generatorSide ? String(period.generatorSide).toUpperCase() : null;
                                                                const rightLabel = `${sideLabel ? `${sideLabel} · ` : ''}${formatMinutes(durationMinutes || 0)}`;
                                                                const clickable = activeTrackReady && activeTrackData?.track;
                                                                return (
                                                                    <Box
                                                                        key={`${period?.startTime || 'period'}-${index}`}
                                                                        onClick={() => clickable && handleRunningPeriodClick(period)}
                                                                        sx={{
                                                                            display: 'grid',
                                                                            gridTemplateColumns: 'auto 1fr auto',
                                                                            gap: 1,
                                                                            alignItems: 'center',
                                                                            padding: 1,
                                                                            borderRadius: 1.5,
                                                                            border: `1px solid ${palette.border}`,
                                                                            background: palette.card,
                                                                            cursor: clickable ? 'pointer' : 'default',
                                                                            transition: 'all 0.2s ease',
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                width: 34,
                                                                                height: 34,
                                                                                borderRadius: '50%',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                backgroundColor: `${palette.crimson}22`,
                                                                                color: palette.crimson,
                                                                                fontWeight: 800,
                                                                                fontSize: 12,
                                                                            }}
                                                                        >
                                                                            G
                                                                        </Box>
                                                                        <Box sx={{ minWidth: 0 }}>
                                                                            <Typography variant="caption" sx={{ color: palette.muted, fontWeight: 600 }}>
                                                                                {timeLabel}
                                                                            </Typography>
                                                                            {address && (
                                                                                <Tooltip title={address}>
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            color: palette.ink,
                                                                                            display: 'block',
                                                                                            whiteSpace: 'nowrap',
                                                                                            overflow: 'hidden',
                                                                                            textOverflow: 'ellipsis',
                                                                                        }}
                                                                                    >
                                                                                        {address}
                                                                                    </Typography>
                                                                                </Tooltip>
                                                                            )}
                                                                        </Box>
                                                                        <Typography variant="caption" sx={{ color: palette.muted, fontWeight: 700 }}>
                                                                            {rightLabel}
                                                                        </Typography>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Stack>
                                                    )}
                                                </Paper>
                                            )}
                                                    </>
                                                );
                                            })()}
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
                                        <Paper sx={{ padding: 2, borderRadius: 2, border: `1px solid ${palette.border}` }}>
                                            <Stack spacing={0.5}>
                                                <Typography variant="caption" sx={{ color: palette.muted }}>Today Total Running</Typography>
                                                {metricsLoading ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 48 }}>
                                                        <CircularProgress size={26} thickness={4} sx={{ color: palette.accentDark }} />
                                                    </Box>
                                                ) : (
                                                    <>
                                                        <Typography variant="h5" sx={{ color: palette.ink, fontWeight: 700 }}>
                                                            {formatDurationVerbose(selectedVehicle?.period?.sumHoursMin || 0)}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: palette.muted }}>
                                                            Union: {formatDurationVerbose(selectedVehicle?.period?.unionHoursMin || 0)}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Stack>
                                        </Paper>
                                        <Paper sx={{ padding: 2, borderRadius: 2, border: `1px solid ${palette.border}` }}>
                                            <Stack spacing={0.5}>
                                                <Typography variant="caption" sx={{ color: palette.muted }}>Estimate Generator Fuel Cost</Typography>
                                                {metricsLoading ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 48 }}>
                                                        <CircularProgress size={26} thickness={4} sx={{ color: palette.accentDark }} />
                                                    </Box>
                                                ) : (
                                                    <>
                                                        <Typography variant="h5" sx={{ color: palette.ink, fontWeight: 700 }}>
                                                            ₹{formatIndianNumber(selectedVehicle?.period?.estimatedCost || 0)}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: palette.muted }}>
                                                            Approx. 2 L/ Hour
                                                        </Typography>
                                                    </>
                                                )}
                                            </Stack>
                                        </Paper>
                                        <Paper sx={{ padding: 2, borderRadius: 2, border: `1px solid ${palette.border}` }}>
                                            <Stack spacing={0.5}>
                                                <Typography variant="caption" sx={{ color: palette.muted }}>Distance Traveled</Typography>
                                                {metricsLoading ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 48 }}>
                                                        <CircularProgress size={26} thickness={4} sx={{ color: palette.accentDark }} />
                                                    </Box>
                                                ) : (
                                                    <Typography variant="h5" sx={{ color: palette.ink, fontWeight: 700 }}>
                                                        {formattedKilometers}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Paper>
                                    </Box>

                                    <Paper sx={{ padding: 2, borderRadius: 2, border: `1px solid ${palette.border}` }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: palette.ink, marginBottom: 1 }}>
                                            Live Status
                                        </Typography>
                                        {currentStatusLoading ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 140 }}>
                                                <CircularProgress size={34} thickness={4} sx={{ color: palette.accentDark }} />
                                            </Box>
                                        ) : (
                                            <Stack spacing={1.5} sx={{ '& > div:last-of-type': { borderBottom: 'none' } }}>
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: { xs: '1fr', md: '140px 1fr 180px' },
                                                        gap: 1.5,
                                                        alignItems: 'center',
                                                        paddingY: 0.5,
                                                        color: palette.muted,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: 0.6,
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    <Box>Generator</Box>
                                                    <Box>Running</Box>
                                                    <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>Sensor</Box>
                                                </Box>
                                                {resolveSides(selectedVehicle).map((side) => {
                                                    const key = side.charAt(0).toUpperCase() + side.slice(1);
                                                    const entry = getRunningEntry(selectedVehicle?.currentRunning, key) || {};
                                                    const durationMin = Number(entry?.durationMin || 0);
                                                    const isRunning = durationMin > 0;
                                                    const runningStatusBadge = getLiveRunningStatusBadge(entry, durationMin);
                                                    const durationText = formatMinutes(durationMin);
                                                    const startText = entry?.startTime
                                                        ? moment(entry.startTime, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true).format('h:mmA DMMM YYYY')
                                                        : '--';
                                                    const isWarnRun = durationMin >= 240 && durationMin < 360;
                                                    const isLongRun = durationMin >= 360;
                                                    let durationColor = palette.emerald;
                                                    let durationBg = 'rgba(21, 128, 61, 0.12)';
                                                    if (isWarnRun) {
                                                        durationColor = palette.amber;
                                                        durationBg = 'rgba(245, 158, 11, 0.12)';
                                                    } else if (isLongRun) {
                                                        durationColor = palette.crimson;
                                                        durationBg = 'rgba(193, 18, 31, 0.12)';
                                                    }
                                                    const sensorLabel = getSensorHealthString(selectedVehicle?.sensorHealth, side);
                                                    const sensorLower = String(sensorLabel || '').toLowerCase();
                                                    let sensorColor = palette.muted;
                                                    let sensorBg = `${palette.border}66`;
                                                    if (sensorLower.includes('cut')) {
                                                        sensorColor = palette.crimson;
                                                        sensorBg = 'rgba(193, 18, 31, 0.12)';
                                                    } else if (sensorLower.includes('good')) {
                                                        sensorColor = palette.emerald;
                                                        sensorBg = 'rgba(21, 128, 61, 0.12)';
                                                    } else if (sensorLower.includes('not monitored') || sensorLower.includes('not installed')) {
                                                        sensorColor = palette.muted;
                                                        sensorBg = `${palette.border}66`;
                                                    }

                                                    return (
                                                        <Box
                                                            key={side}
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: { xs: '1fr', md: '140px 1fr 180px' },
                                                                gap: 1.5,
                                                                alignItems: 'center',
                                                                paddingY: 0.8,
                                                                borderBottom: `1px solid ${palette.border}55`,
                                                            }}
                                                        >
                                                            <Box>
                                                                <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', color: palette.ink, fontWeight: 700 }}>
                                                                    {side}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                {runningStatusBadge ? (
                                                                    <Chip
                                                                        label={runningStatusBadge.label}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: runningStatusBadge.bg,
                                                                            color: runningStatusBadge.color,
                                                                            fontWeight: 700,
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            mt: 0.2,
                                                                            px: 1.2,
                                                                            py: 0.3,
                                                                            borderRadius: 1.5,
                                                                            backgroundColor: durationBg,
                                                                            color: durationColor,
                                                                            fontWeight: 700,
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: 0.6,
                                                                            '@keyframes durationPulse': {
                                                                                '0%': { boxShadow: `0 0 0 0 ${durationColor}33` },
                                                                                '70%': { boxShadow: `0 0 0 10px ${durationColor}00` },
                                                                                '100%': { boxShadow: `0 0 0 0 ${durationColor}00` },
                                                                            },
                                                                            animation: 'durationPulse 3s ease-out infinite',
                                                                        }}
                                                                    >
                                                                        {durationText}
                                                                    </Box>
                                                                )}
                                                                {!runningStatusBadge && isRunning && (
                                                                    <Typography variant="caption" sx={{ color: palette.muted, display: 'block', marginTop: 0.3 }}>
                                                                        From {startText}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                                                                <Box>
                                                                    <Chip
                                                                        label={sensorLabel}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: sensorBg,
                                                                            color: sensorColor,
                                                                            fontWeight: 600,
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        )}
                                    </Paper>

                                    <Paper
                                        sx={{
                                            padding: 2,
                                            borderRadius: 2,
                                            border: '1px solid rgba(193, 18, 31, 0.18)',
                                            backgroundColor: '#fffefe',
                                        }}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: palette.ink, marginBottom: 1 }}>
                                            Active Issues
                                        </Typography>
                                        {activeIssues.length === 0 && (
                                            <Typography variant="body2" sx={{ color: palette.muted }}>
                                                No active issues.
                                            </Typography>
                                        )}
                                        {activeIssues.length > 0 && (
                                            <Stack spacing={0.8}>
                                                {activeIssues.map((issue, index) => {
                                                    const tone = resolveActiveIssueTone(issue);
                                                    const label = issue?.message || issue?.code || 'Issue';
                                                    const severityLabel = issue?.severity
                                                        ? String(issue.severity).toUpperCase()
                                                        : null;
                                                    return (
                                                        <Box
                                                            key={`active-issue-${issue?.code || index}`}
                                                            sx={{
                                                                padding: 1.15,
                                                                borderRadius: 1.5,
                                                                backgroundColor: tone.bg,
                                                                border: `1px solid ${tone.border}`,
                                                                boxShadow: '0 1px 0 rgba(153, 27, 27, 0.06)',
                                                            }}
                                                        >
                                                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                                                <Box sx={{ minWidth: 0 }}>
                                                                    <Typography variant="body2" sx={{ color: tone.color, fontWeight: 700, lineHeight: 1.35 }}>
                                                                        {label}
                                                                    </Typography>
                                                                </Box>
                                                                {severityLabel && (
                                                                    <Chip
                                                                        label={severityLabel}
                                                                        size="small"
                                                                        sx={{
                                                                            height: 22,
                                                                            backgroundColor: tone.chipBg,
                                                                            color: tone.color,
                                                                            fontWeight: 700,
                                                                            border: `1px solid ${tone.border}`,
                                                                        }}
                                                                    />
                                                                )}
                                                            </Stack>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        )}
                                    </Paper>


                                    {showRunningPeriods && (
                                        <Paper sx={{ padding: 2, borderRadius: 2, border: `1px solid ${palette.border}` }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: palette.ink, marginBottom: 1 }}>
                                                Running Periods
                                            </Typography>
                                            {runningPeriods.length === 0 && (
                                                <Typography variant="body2" sx={{ color: palette.muted }}>No running periods yet.</Typography>
                                            )}
                                            {runningPeriods.length > 0 && (
                                                <Stack spacing={1.5}>
                                                    {runningPeriods.map((period, index) => (
                                                        <Box
                                                            key={`${period?.startTime || 'period'}-${index}`}
                                                            sx={{
                                                                padding: 1.5,
                                                                borderRadius: 2,
                                                                background: palette.soft,
                                                                border: `1px solid ${palette.border}`,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' },
                                                                    gap: 1.5,
                                                                    alignItems: 'start',
                                                                }}
                                                            >
                                                                <Box>
                                                                    <Typography variant="caption" sx={{ color: palette.muted }}>
                                                                        From
                                                                    </Typography>
                                                                    <Typography variant="subtitle2" sx={{ color: palette.ink }}>
                                                                        {formatDateTime(period?.startTime)}
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ color: palette.muted }}>
                                                                        {period?.startAddress?.net_addr || '--'}
                                                                    </Typography>
                                                                </Box>
                                                                <Box>
                                                                    <Typography variant="caption" sx={{ color: palette.muted }}>
                                                                        To
                                                                    </Typography>
                                                                    <Typography variant="subtitle2" sx={{ color: palette.ink }}>
                                                                        {formatDateTime(period?.endTime)}
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ color: palette.muted }}>
                                                                        {period?.endAddress?.net_addr || '--'}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                                                                    <Chip
                                                                        label={`${String(period?.generatorSide || '').toUpperCase()} · ${formatMinutes(period?.durationMinutes || 0)}`}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: Number(period?.durationMinutes || 0) < 10
                                                                                ? `${palette.border}66`
                                                                                : palette.accentSoft,
                                                                            color: Number(period?.durationMinutes || 0) < 10
                                                                                ? palette.muted
                                                                                : palette.accentDark,
                                                                            fontWeight: Number(period?.durationMinutes || 0) < 10 ? 500 : 700,
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            )}
                                        </Paper>
                                    )}

                                    {isAdmin && !gensetSetupLoading && (
                                        <Paper sx={{ padding: 2, borderRadius: 2, border: `1px solid ${palette.border}` }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: palette.ink, marginBottom: 1 }}>
                                                Setup Details
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                                                    gap: 1.5,
                                                    marginBottom: 1.5,
                                                }}
                                            >
                                                {[
                                                    { label: 'Setup Type', value: resolveSetupType(selectedVehicle) },
                                                    { label: 'Device Type', value: selectedVehicle?.additionalInfo?.deviceType || '--' },
                                                    { label: 'Listener Port', value: selectedVehicle?.additionalInfo?.listenerPort || '--' },
                                                    { label: 'Truck Voltage', value: selectedVehicle?.additionalInfo?.truckVoltage || '--' },
                                                ].map((item) => (
                                                    <Box
                                                        key={item.label}
                                                        sx={{
                                                            padding: 1,
                                                            borderRadius: 1.5,
                                                            border: `1px solid ${palette.border}66`,
                                                            background: palette.soft,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: palette.muted,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: 0.6,
                                                                fontSize: 11,
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {item.label}
                                                        </Typography>
                                                        <Typography variant="subtitle2" sx={{ color: palette.ink, fontWeight: 700, marginTop: 0.4 }}>
                                                            {item.value || '--'}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                            <Typography variant="subtitle2" sx={{ color: palette.ink, marginBottom: 0.5 }}>
                                                Issues
                                            </Typography>
                                            {setupIssues.length === 0 && (
                                                <Typography variant="body2" sx={{ color: palette.muted }}>No issues detected.</Typography>
                                            )}
                                            {setupIssues.length > 0 && (
                                                <Stack spacing={0.6}>
                                                    {setupIssues.map((issue, index) => {
                                                        const tone = resolveIssueTone(issue);
                                                        const label = issue?.message || issue?.code || 'Issue';
                                                        const severityLabel = issue?.severity
                                                            ? String(issue.severity).toUpperCase()
                                                            : null;
                                                        return (
                                                            <Box
                                                                key={`${issue?.code || 'issue'}-${index}`}
                                                                sx={{
                                                                    padding: 0.9,
                                                                    borderRadius: 1.5,
                                                                    backgroundColor: tone.bg,
                                                                    border: `1px solid ${tone.color}33`,
                                                                }}
                                                            >
                                                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                                                    <Typography variant="body2" sx={{ color: tone.color, fontWeight: 600 }}>
                                                                        {label}
                                                                    </Typography>
                                                                    {severityLabel && (
                                                                        <Chip
                                                                            label={severityLabel}
                                                                            size="small"
                                                                            sx={{
                                                                                height: 22,
                                                                                backgroundColor: '#ffffff',
                                                                                color: tone.color,
                                                                                fontWeight: 700,
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Stack>
                                                            </Box>
                                                        );
                                                    })}
                                                </Stack>
                                            )}
                                            {selectedVehicle?.sensorConfiguration && (
                                                <Box sx={{ marginTop: 2 }}>
                                                    <Typography variant="subtitle1" sx={{ color: palette.ink, marginBottom: 1, fontWeight: 600 }}>
                                                        Sensor Configuration
                                                    </Typography>
                                                    <Stack spacing={1.5}>
                                                        {(() => {
                                                            const config = selectedVehicle.sensorConfiguration || {};
                                                            const sensorItems = buildSensorItems(config.generatorSensor);
                                                            const wireSensorItems = buildSensorItems(config.generatorWireSensor);
                                                            const eventItems = buildEventItems(config.generatorEvent);
                                                            const wireEventItems = buildEventItems(config.generatorWireEvent);
                                                            const analogCutoffMap = selectedVehicle?.additionalInfo?.analogCutoff || {};

                                                            const resolveAnalogCutoff = (parameter) => {
                                                                const key = String(parameter || '').toLowerCase();
                                                                if (!['mdi1', 'mdi2'].includes(key)) return null;
                                                                const raw = analogCutoffMap?.[key] ?? analogCutoffMap?.[key.toUpperCase?.()] ?? null;
                                                                if (raw === null || raw === undefined || raw === '') return null;
                                                                const parsed = Number(String(raw).replace(/[^0-9.-]/g, ''));
                                                                if (!Number.isFinite(parsed)) return null;
                                                                const converted = parsed > 100 ? (parsed / 1000).toFixed(1) : String(parsed);
                                                                return `${converted} V`;
                                                            };

                                                            const getSideChipStyle = (side) => {
                                                                const key = String(side || '').toLowerCase();
                                                                if (key.includes('left')) {
                                                                    return { backgroundColor: 'rgba(124, 58, 237, 0.16)', color: '#6d28d9' };
                                                                }
                                                                if (key.includes('right')) {
                                                                    return { backgroundColor: 'rgba(236, 72, 153, 0.16)', color: '#db2777' };
                                                                }
                                                                return { backgroundColor: palette.accentSoft, color: palette.accentDark };
                                                            };

                                                            const renderSensorList = (items, label) => (
                                                                <SectionBlock title={label}>
                                                                    {items.length === 0 && (
                                                                        <Typography variant="caption" sx={{ color: palette.muted }}>
                                                                            No entries available.
                                                                        </Typography>
                                                                    )}
                                                                    <Stack spacing={1}>
                                                                        {items.map((item) => {
                                                                            const analogValue = resolveAnalogCutoff(item.parameter);
                                                                            const sideLabel = String(item.side || '').toUpperCase();
                                                                            const sideChipStyle = getSideChipStyle(item.side);
                                                                            return (
                                                                                <Box
                                                                                    key={item.key}
                                                                                    sx={{
                                                                                        padding: 1.2,
                                                                                        borderRadius: 2,
                                                                                        background: `linear-gradient(135deg, ${palette.soft}, #ffffff)`,
                                                                                        border: `1px solid ${palette.border}`,
                                                                                        boxShadow: '0 6px 14px rgba(16, 42, 67, 0.06)',
                                                                                    }}
                                                                                >
                                                                                    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                                                                                        <Typography variant="subtitle2" sx={{ color: palette.ink, fontWeight: 700 }}>
                                                                                            {item.name}
                                                                                        </Typography>
                                                                                    </Stack>
                                                                                    <Box
                                                                                        sx={{
                                                                                            display: 'grid',
                                                                                            gridTemplateColumns: {
                                                                                                xs: 'auto 1fr',
                                                                                                md: analogValue ? 'auto 1fr 1.4fr 1fr' : 'auto 1fr 1.6fr',
                                                                                            },
                                                                                            gap: 1.5,
                                                                                            marginTop: 0.8,
                                                                                            alignItems: 'center',
                                                                                        }}
                                                                                    >
                                                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                            <Chip
                                                                                                label={sideLabel}
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    ...sideChipStyle,
                                                                                                    fontWeight: 700,
                                                                                                    letterSpacing: 0.4,
                                                                                                }}
                                                                                            />
                                                                                        </Box>
                                                                                        <Box>
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    color: palette.muted,
                                                                                                    textTransform: 'uppercase',
                                                                                                    letterSpacing: 0.6,
                                                                                                    fontSize: 11,
                                                                                                    fontWeight: 700,
                                                                                                }}
                                                                                            >
                                                                                                Parameter
                                                                                            </Typography>
                                                                                            <Typography variant="subtitle2" sx={{ color: palette.ink, fontWeight: 700, marginTop: 0.2 }}>
                                                                                                {item.parameter || '--'}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                        <Box>
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    color: palette.muted,
                                                                                                    textTransform: 'uppercase',
                                                                                                    letterSpacing: 0.6,
                                                                                                    fontSize: 11,
                                                                                                    fontWeight: 700,
                                                                                                }}
                                                                                            >
                                                                                                Logic
                                                                                            </Typography>
                                                                                            {item.logic?.length > 0 ? (
                                                                                                <Stack direction="row" spacing={0.6} sx={{ marginTop: 0.4, flexWrap: 'wrap' }}>
                                                                                                    {item.logic.map((entry) => (
                                                                                                        <Chip
                                                                                                            key={`${item.key}-${entry}`}
                                                                                                            label={entry}
                                                                                                            size="small"
                                                                                                            sx={{
                                                                                                                backgroundColor: `${palette.border}66`,
                                                                                                                color: palette.ink,
                                                                                                                fontWeight: 600,
                                                                                                            }}
                                                                                                        />
                                                                                                    ))}
                                                                                                </Stack>
                                                                                            ) : (
                                                                                                <Typography variant="subtitle2" sx={{ color: palette.ink, fontWeight: 700, marginTop: 0.2 }}>
                                                                                                    --
                                                                                                </Typography>
                                                                                            )}
                                                                                        </Box>
                                                                                        {analogValue && (
                                                                                            <Box>
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    sx={{
                                                                                                        color: palette.muted,
                                                                                                        textTransform: 'uppercase',
                                                                                                        letterSpacing: 0.6,
                                                                                                        fontSize: 11,
                                                                                                        fontWeight: 700,
                                                                                                    }}
                                                                                                >
                                                                                                    Analog Cutoff
                                                                                                </Typography>
                                                                                                <Typography variant="subtitle2" sx={{ color: palette.ink, fontWeight: 700, marginTop: 0.2 }}>
                                                                                                    {analogValue}
                                                                                                </Typography>
                                                                                            </Box>
                                                                                        )}
                                                                                    </Box>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                    </Stack>
                                                                </SectionBlock>
                                                            );

                                                            const renderEventList = (items, label) => (
                                                                <SectionBlock title={label}>
                                                                    {items.length === 0 && (
                                                                        <Typography variant="caption" sx={{ color: palette.muted }}>
                                                                            No entries available.
                                                                        </Typography>
                                                                    )}
                                                                    <Stack spacing={1}>
                                                                        {items.map((item) => {
                                                                            const sideLabel = String(item.side || '').toUpperCase();
                                                                            const sideChipStyle = getSideChipStyle(item.side);
                                                                            return (
                                                                                <Box
                                                                                    key={item.key}
                                                                                    sx={{
                                                                                        padding: 1.2,
                                                                                        borderRadius: 2,
                                                                                        background: `linear-gradient(135deg, ${palette.soft}, #ffffff)`,
                                                                                        border: `1px solid ${palette.border}`,
                                                                                        boxShadow: '0 6px 14px rgba(16, 42, 67, 0.06)',
                                                                                    }}
                                                                                >
                                                                                    <Box
                                                                                        sx={{
                                                                                            display: 'grid',
                                                                                            gridTemplateColumns: { xs: 'auto 1fr', md: 'auto 1.6fr 1fr 1fr' },
                                                                                            gap: 1.5,
                                                                                            marginTop: 0.6,
                                                                                            alignItems: 'center',
                                                                                        }}
                                                                                    >
                                                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                            <Chip
                                                                                                label={sideLabel}
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    ...sideChipStyle,
                                                                                                    fontWeight: 700,
                                                                                                    letterSpacing: 0.4,
                                                                                                }}
                                                                                            />
                                                                                        </Box>
                                                                                        <Box>
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    color: palette.muted,
                                                                                                    textTransform: 'uppercase',
                                                                                                    letterSpacing: 0.6,
                                                                                                    fontSize: 11,
                                                                                                    fontWeight: 700,
                                                                                                }}
                                                                                            >
                                                                                                Event Name
                                                                                            </Typography>
                                                                                            <Typography variant="subtitle2" sx={{ color: palette.ink, fontWeight: 700, marginTop: 0.2 }}>
                                                                                                {item.name || '--'}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                        <Box>
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    color: palette.muted,
                                                                                                    textTransform: 'uppercase',
                                                                                                    letterSpacing: 0.6,
                                                                                                    fontSize: 11,
                                                                                                    fontWeight: 700,
                                                                                                }}
                                                                                            >
                                                                                                Parameter
                                                                                            </Typography>
                                                                                            <Typography variant="subtitle2" sx={{ color: palette.ink, fontWeight: 700, marginTop: 0.2 }}>
                                                                                                {item.parameter || '--'}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                        <Box>
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    color: palette.muted,
                                                                                                    textTransform: 'uppercase',
                                                                                                    letterSpacing: 0.6,
                                                                                                    fontSize: 11,
                                                                                                    fontWeight: 700,
                                                                                                }}
                                                                                            >
                                                                                                Checked Value
                                                                                            </Typography>
                                                                                            <Typography variant="subtitle2" sx={{ color: palette.ink, fontWeight: 700, marginTop: 0.2 }}>
                                                                                                {item.value ?? '--'}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    </Box>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                    </Stack>
                                                                </SectionBlock>
                                                            );

                                                            return (
                                                                <Stack spacing={1.5}>
                                                                    {renderSensorList(sensorItems, 'Generator Sensors')}
                                                                    {renderEventList(eventItems, 'Generator Events')}
                                                                    {renderSensorList(wireSensorItems, 'Wire Sensors')}
                                                                    {renderEventList(wireEventItems, 'Wire Events')}
                                                                </Stack>
                                                            );
                                                        })()}
                                                    </Stack>
                                                </Box>
                                            )}
                                        </Paper>
                                    )}
                                </Stack>
                            )}
                        </Paper>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflowY: 'auto', paddingRight: 0.5, gridArea: 'summary', pointerEvents: 'auto' }}>
                        <Paper sx={{ padding: 2, borderRadius: 3, border: `1px solid ${palette.border}` }}>
                            {fullHydrationLoading ? (
                                <Box
                                    sx={{
                                        minHeight: 220,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 2,
                                        background: `radial-gradient(circle at top, ${palette.accentSoft}, #ffffff)`,
                                        border: `1px dashed ${palette.border}`,
                                    }}
                                >
                                    <CircularProgress size={54} thickness={3.6} sx={{ color: palette.emerald }} />
                                </Box>
                            ) : (
                                <>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ marginBottom: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: palette.ink }}>{fleetSummaryTitle}</Typography>
                                        <Stack direction="row" spacing={0.8} alignItems="center">
                                            {batchHydrationActive && (
                                                <Tooltip title={hydrationProgressLabel ? `Hydrating ${hydrationProgressLabel}` : 'Hydrating fleet summary'}>
                                                    <Box
                                                        sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 0.45,
                                                            '& .fleet-dot': {
                                                                width: 6,
                                                                height: 6,
                                                                borderRadius: '50%',
                                                                backgroundColor: palette.accentDark,
                                                                opacity: 0.25,
                                                                animation: 'fleetSummaryDotPulse 1.2s infinite ease-in-out',
                                                            },
                                                            '& .fleet-dot:nth-of-type(1)': { animationDelay: '0s' },
                                                            '& .fleet-dot:nth-of-type(2)': { animationDelay: '0.2s' },
                                                            '& .fleet-dot:nth-of-type(3)': { animationDelay: '0.4s' },
                                                            '@keyframes fleetSummaryDotPulse': {
                                                                '0%, 80%, 100%': { opacity: 0.25, transform: 'translateY(0)' },
                                                                '40%': { opacity: 1, transform: 'translateY(-2px)' },
                                                            },
                                                        }}
                                                    >
                                                        <Box component="span" className="fleet-dot" />
                                                        <Box component="span" className="fleet-dot" />
                                                        <Box component="span" className="fleet-dot" />
                                                    </Box>
                                                </Tooltip>
                                            )}
                                            {statusState.refreshing && (
                                                <Chip label="Refreshing" size="small" sx={{ backgroundColor: palette.accentSoft, color: palette.accentDark }} />
                                            )}
                                        </Stack>
                                    </Stack>
                                    {statusState.cacheExpired && (
                                        <Typography variant="caption" sx={{ color: palette.crimson }}>
                                            Cache expired. Rehydration in progress.
                                        </Typography>
                                    )}
                                    <Stack spacing={1.5}>
                                        <Box
                                            sx={{
                                                padding: 1.5,
                                                borderRadius: 2.5,
                                                border: '1px solid rgba(14, 165, 160, 0.25)',
                                                background: 'linear-gradient(135deg, rgba(14, 165, 160, 0.14), rgba(59, 130, 246, 0.08))',
                                                boxShadow: '0 12px 22px rgba(16, 42, 67, 0.08)',
                                            }}
                                        >
                                            <Stack spacing={1}>
                                                {fleetSummaryPrimary.map((item) => {
                                                    const Icon = item.icon;
                                                    const isClickable = Boolean(item.filterMode);
                                                    const isDisabled = isClickable && Number(item.value) <= 0;
                                                    const isActive = isClickable && filterMode === item.filterMode;
                                                    return (
                                                        <Box
                                                            key={item.key}
                                                            onClick={() => {
                                                                if (!isClickable || isDisabled) return;
                                                                handleFleetSummaryFilter(item.filterMode);
                                                            }}
                                                            sx={{
                                                                padding: 1.1,
                                                                borderRadius: 2,
                                                                border: `1px solid ${isActive ? item.color : palette.border}`,
                                                                background: isActive ? `${item.color}14` : 'rgba(255, 255, 255, 0.78)',
                                                                boxShadow: '0 6px 14px rgba(16, 42, 67, 0.08)',
                                                                cursor: isClickable ? (isDisabled ? 'not-allowed' : 'pointer') : 'default',
                                                                opacity: isDisabled ? 0.55 : 1,
                                                                transition: 'all 0.2s ease',
                                                                '&:hover': isClickable && !isDisabled
                                                                    ? {
                                                                        borderColor: item.color,
                                                                        boxShadow: `0 8px 18px ${item.color}22`,
                                                                    }
                                                                    : {},
                                                            }}
                                                        >
                                                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Box
                                                                        sx={{
                                                                            width: 36,
                                                                            height: 36,
                                                                            borderRadius: '50%',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            backgroundColor: `${item.color}22`,
                                                                            color: item.color,
                                                                        }}
                                                                    >
                                                                        <Icon fontSize="small" />
                                                                    </Box>
                                                                    <Typography variant="body2" sx={{ color: palette.muted, fontWeight: 600 }}>
                                                                        {item.label}
                                                                    </Typography>
                                                                </Stack>
                                                                <Typography variant="subtitle1" sx={{ color: palette.ink, fontWeight: 700, fontSize: 11 }}>
                                                                    {item.value}
                                                                </Typography>
                                                            </Stack>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        </Box>
                                        <Box
                                            sx={{
                                                padding: 1.5,
                                                borderRadius: 2.5,
                                                border: '1px solid rgba(244, 63, 94, 0.28)',
                                                background: 'linear-gradient(135deg, rgba(244, 63, 94, 0.12), rgba(253, 164, 175, 0.12))',
                                                boxShadow: '0 12px 22px rgba(16, 42, 67, 0.08)',
                                            }}
                                        >
                                            <Stack spacing={1}>
                                                {fleetSummaryIssues.map((item) => {
                                                    const Icon = item.icon;
                                                    const isClickable = Boolean(item.filterMode);
                                                    const isDisabled = isClickable && Number(item.value) <= 0;
                                                    const isActive = isClickable && filterMode === item.filterMode;
                                                    return (
                                                        <Box
                                                            key={item.key}
                                                            onClick={() => {
                                                                if (!isClickable || isDisabled) return;
                                                                handleFleetSummaryFilter(item.filterMode);
                                                            }}
                                                            sx={{
                                                                padding: 1.1,
                                                                borderRadius: 2,
                                                                border: `1px solid ${isActive ? item.color : palette.border}`,
                                                                background: isActive ? `${item.color}14` : 'rgba(255, 255, 255, 0.78)',
                                                                boxShadow: '0 6px 14px rgba(16, 42, 67, 0.08)',
                                                                cursor: isClickable ? (isDisabled ? 'not-allowed' : 'pointer') : 'default',
                                                                opacity: isDisabled ? 0.55 : 1,
                                                                transition: 'all 0.2s ease',
                                                                '&:hover': isClickable && !isDisabled
                                                                    ? {
                                                                        borderColor: item.color,
                                                                        boxShadow: `0 8px 18px ${item.color}22`,
                                                                    }
                                                                    : {},
                                                            }}
                                                        >
                                                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Box
                                                                        sx={{
                                                                            width: 36,
                                                                            height: 36,
                                                                            borderRadius: '50%',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            backgroundColor: `${item.color}22`,
                                                                            color: item.color,
                                                                        }}
                                                                    >
                                                                        <Icon fontSize="small" />
                                                                    </Box>
                                                                    <Typography variant="body2" sx={{ color: palette.muted, fontWeight: 600 }}>
                                                                        {item.label}
                                                                    </Typography>
                                                                </Stack>
                                                                <Typography variant="subtitle1" sx={{ color: palette.ink, fontWeight: 700, fontSize: 10 }}>
                                                                    {item.value}
                                                                </Typography>
                                                            </Stack>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </>
                            )}
                        </Paper>

                        <Paper sx={{ padding: 2, borderRadius: 3, border: `1px solid ${palette.border}` }}>
                            {gensetSetupLoading ? (
                                <Box
                                    sx={{
                                        minHeight: 200,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 2,
                                        background: `linear-gradient(135deg, #ffffff, ${palette.accentSoft})`,
                                        border: `1px solid ${palette.border}`,
                                    }}
                                >
                                    <CircularProgress size={58} thickness={4.2} sx={{ color: palette.accentDark }} />
                                </Box>
                            ) : (
                                <>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" marginBottom={1.2}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: palette.ink }}>Setup Summary</Typography>
                                        <Button
                                            size="small"
                                            startIcon={<RefreshIcon />}
                                            onClick={() => dispatch(fetchGensetSetup({ user_id: userId, isAdmin: Boolean(isAdmin) }))}
                                            disabled={setupState.loading}
                                        >
                                            Refresh Setup
                                        </Button>
                                    </Stack>
                                    {setupState.error && (
                                        <Typography variant="caption" sx={{ color: palette.crimson }}>
                                            {setupState.error}
                                        </Typography>
                                    )}
                                    <Box
                                        sx={{
                                            padding: 1.4,
                                            borderRadius: 2,
                                            border: `1px solid ${palette.border}`,
                                            background: `linear-gradient(135deg, ${palette.accentSoft}, #ffffff)`,
                                            boxShadow: '0 8px 16px rgba(16, 42, 67, 0.06)',
                                        }}
                                    >
                                        <Stack spacing={0.8}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Typography variant="caption" sx={{ color: palette.muted, fontWeight: 700, letterSpacing: 0.4 }}>
                                                    Generator Vehicles
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: palette.ink, fontWeight: 700 }}>
                                                    {setupFleet?.generatorVehiclesCount || 0}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Typography variant="caption" sx={{ color: palette.muted, fontWeight: 700, letterSpacing: 0.4 }}>
                                                    Valid Configurations
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: palette.ink, fontWeight: 700 }}>
                                                    {setupFleet?.validVehiclesCount || 0}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                    <Box
                                        sx={{
                                            marginTop: 1.5,
                                            padding: 1.2,
                                            borderRadius: 2,
                                            border: `1px solid ${palette.border}`,
                                            background: palette.soft,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: palette.muted,
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.6,
                                                fontWeight: 700,
                                            }}
                                        >
                                            Setup Types
                                        </Typography>
                                        <Stack spacing={0.5} sx={{ marginTop: 0.6 }}>
                                            {setupCounts.map(([label, value]) => {
                                                const active = setupFilter === label;
                                                return (
                                                    <Box
                                                        key={label}
                                                        onClick={() => handleSetupFilterClick(label)}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 1,
                                                            cursor: 'pointer',
                                                            paddingY: 0.4,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: active ? palette.accentDark : palette.muted,
                                                                fontWeight: active ? 700 : 500,
                                                                '&:hover': { color: palette.accentDark },
                                                            }}
                                                        >
                                                            {label}
                                                        </Typography>
                                                        <Chip
                                                            label={value}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: active ? palette.accentSoft : `${palette.border}66`,
                                                                color: active ? palette.accentDark : palette.ink,
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                            {setupCounts.length === 0 && (
                                                <Typography variant="caption" sx={{ color: palette.muted }}>No setup data yet.</Typography>
                                            )}
                                        </Stack>
                                    </Box>
                                </>
                            )}
                        </Paper>
                        {isAdmin && !gensetSetupLoading && (
                            <Paper sx={{ padding: 1.6, borderRadius: 3, border: `1px solid ${palette.border}`, background: '#f8fafc' }}>
                                <Stack spacing={1.2}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.ink }}>
                                        Admin
                                    </Typography>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: palette.muted, fontWeight: 700, letterSpacing: 0.5 }}>
                                            Issues
                                        </Typography>
                                        <Stack spacing={0.3} sx={{ marginTop: 0.4 }}>
                                            {issueCounts.map((item) => {
                                                const active = issueFilter === item.code;
                                                const tone = resolveIssueTone(item);
                                                const severityLabel = item?.severity
                                                    ? String(item.severity).toUpperCase()
                                                    : null;
                                                let typeLabel = null;
                                                if (item?.issueType) {
                                                    const normalized = String(item.issueType).toLowerCase();
                                                    if (normalized === 'runtime' || normalized === 'runtimeissue' || normalized === 'run_time') {
                                                        typeLabel = 'RUNTIME';
                                                    } else if (normalized === 'setup') {
                                                        typeLabel = 'SETUP';
                                                    } else {
                                                        typeLabel = String(item.issueType).toUpperCase();
                                                    }
                                                }
                                                return (
                                                    <Box
                                                        key={item.code}
                                                        onClick={() => handleIssueFilterClick(item.code)}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 1,
                                                            cursor: 'pointer',
                                                            paddingY: 0.6,
                                                            borderBottom: `1px dashed ${palette.border}`,
                                                            '&:last-of-type': { borderBottom: 'none' },
                                                        }}
                                                    >
                                                        <Stack spacing={0.2} sx={{ minWidth: 0, flex: 1 }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: active ? palette.ink : palette.muted,
                                                                    fontWeight: active ? 700 : 600,
                                                                    lineHeight: 1.35,
                                                                }}
                                                            >
                                                                {item.label}
                                                            </Typography>
                                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                                                {severityLabel && (
                                                                    <Stack direction="row" spacing={0.6} alignItems="center">
                                                                        <Box
                                                                            sx={{
                                                                                width: 6,
                                                                                height: 6,
                                                                                borderRadius: '50%',
                                                                                backgroundColor: tone.color,
                                                                            }}
                                                                        />
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                color: tone.color,
                                                                                fontWeight: 700,
                                                                                letterSpacing: 0.4,
                                                                            }}
                                                                        >
                                                                            {severityLabel}
                                                                        </Typography>
                                                                    </Stack>
                                                                )}
                                                                {typeLabel && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: palette.muted,
                                                                            fontWeight: 700,
                                                                            letterSpacing: 0.4,
                                                                        }}
                                                                    >
                                                                        {typeLabel}
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </Stack>
                                                        <Box
                                                            sx={{
                                                                minWidth: 28,
                                                                height: 28,
                                                                borderRadius: '50%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                backgroundColor: active ? 'rgba(193, 18, 31, 0.12)' : 'rgba(15, 23, 42, 0.06)',
                                                                color: active ? palette.crimson : palette.ink,
                                                                fontWeight: 700,
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            {item.count}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                            {issueCounts.length === 0 && (
                                                <Typography variant="caption" sx={{ color: palette.muted }}>No issues aggregated.</Typography>
                                            )}
                                        </Stack>
                                    </Box>
                                    <Divider />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: palette.muted, fontWeight: 700, letterSpacing: 0.5 }}>
                                            Device Type
                                        </Typography>
                                        <Stack spacing={0.3} sx={{ marginTop: 0.4 }}>
                                            {metadataCounts.deviceTypes.map((item) => {
                                                const active = deviceTypeFilter === item.label;
                                                return (
                                                    <Box
                                                        key={`device-${item.label}`}
                                                        onClick={() => handleDeviceTypeFilterClick(item.label)}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 1,
                                                            cursor: 'pointer',
                                                            paddingY: 0.2,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: active ? palette.accentDark : palette.muted,
                                                                fontWeight: active ? 700 : 500,
                                                                '&:hover': { color: palette.accentDark },
                                                            }}
                                                        >
                                                            {item.label}
                                                        </Typography>
                                                        <Chip
                                                            label={item.value}
                                                            size="small"
                                                            sx={{
                                                                height: 22,
                                                                backgroundColor: active ? palette.accentSoft : `${palette.border}66`,
                                                                color: active ? palette.accentDark : palette.ink,
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                            {metadataCounts.deviceTypes.length === 0 && (
                                                <Typography variant="caption" sx={{ color: palette.muted }}>No device types reported.</Typography>
                                            )}
                                        </Stack>
                                    </Box>
                                    <Divider />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: palette.muted, fontWeight: 700, letterSpacing: 0.5 }}>
                                            Listener Port
                                        </Typography>
                                        <Stack spacing={0.3} sx={{ marginTop: 0.4 }}>
                                            {metadataCounts.listenerPorts.map((item) => {
                                                const active = listenerPortFilter === item.label;
                                                return (
                                                    <Box
                                                        key={`port-${item.label}`}
                                                        onClick={() => handleListenerPortFilterClick(item.label)}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 1,
                                                            cursor: 'pointer',
                                                            paddingY: 0.2,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: active ? palette.accentDark : palette.muted,
                                                                fontWeight: active ? 700 : 500,
                                                                '&:hover': { color: palette.accentDark },
                                                            }}
                                                        >
                                                            {item.label}
                                                        </Typography>
                                                        <Chip
                                                            label={item.value}
                                                            size="small"
                                                            sx={{
                                                                height: 22,
                                                                backgroundColor: active ? palette.accentSoft : `${palette.border}66`,
                                                                color: active ? palette.accentDark : palette.ink,
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                            {metadataCounts.listenerPorts.length === 0 && (
                                                <Typography variant="caption" sx={{ color: palette.muted }}>No listener ports reported.</Typography>
                                            )}
                                        </Stack>
                                    </Box>
                                    <Divider />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: palette.muted, fontWeight: 700, letterSpacing: 0.5 }}>
                                            Truck Voltage
                                        </Typography>
                                        <Stack spacing={0.3} sx={{ marginTop: 0.4 }}>
                                            {metadataCounts.truckVoltages.map((item) => {
                                                const active = truckVoltageFilter === item.label;
                                                return (
                                                    <Box
                                                        key={`voltage-${item.label}`}
                                                        onClick={() => handleTruckVoltageFilterClick(item.label)}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 1,
                                                            cursor: 'pointer',
                                                            paddingY: 0.2,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: active ? palette.accentDark : palette.muted,
                                                                fontWeight: active ? 700 : 500,
                                                                '&:hover': { color: palette.accentDark },
                                                            }}
                                                        >
                                                            {item.label}
                                                        </Typography>
                                                        <Chip
                                                            label={item.value}
                                                            size="small"
                                                            sx={{
                                                                height: 22,
                                                                backgroundColor: active ? palette.accentSoft : `${palette.border}66`,
                                                                color: active ? palette.accentDark : palette.ink,
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                            {metadataCounts.truckVoltages.length === 0 && (
                                                <Typography variant="caption" sx={{ color: palette.muted }}>No truck voltages reported.</Typography>
                                            )}
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Paper>
                        )}
                    </Box>
                </Box>
            )}
        </>
    )}
            </Box>
        </Box>
        <GenfleetReportForm open={reportFormOpen} onClose={handleCloseReportForm} />
        </>
    );
};

export default GensetDashboard;
